import { esc } from '../escape.js';
import { formatMoney, currencySymbol } from '../currency.js';
import { openLightbox } from '../imageLightbox.js';

// Top-level Inventory panel - a full inventory management system (2026-07-17
// overhaul, mockup-driven): Items (table + detail drawer with "Used by"
// product links), Purchase Orders, Suppliers (strictly optional), Stock
// Counts, and History. Shares the Timesheets panel's structural shell
// (.ts-layout sidebar chrome); the content area is styled fresh per the UI
// quality bar. Vocabulary: product = thing sold, item/ingredient = inventory,
// stock = on-hand count.

// Quantities are plain numbers in the item's own unit - never money. Trim
// trailing zeros so "4000" doesn't render as "4000.00" but "2.5" survives.
const fmtQty = (n) => {
  const num = Number(n ?? 0);
  if (Number.isInteger(num)) return num.toLocaleString('en-US');
  return parseFloat(num.toFixed(3)).toLocaleString('en-US', { maximumFractionDigits: 3 });
};

const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';

// Relative "Updated" label for table rows.
const fmtRel = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  const mins = Math.max(0, Math.floor((Date.now() - d) / 60000));
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 60 * 24 && d.toDateString() === new Date().toDateString())
    return `Today, ${fmtTime(iso)}`;
  if (d.toDateString() === new Date(Date.now() - 86400000).toDateString())
    return `Yesterday, ${fmtTime(iso)}`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Displayed PO number: internal sequential integer -> "PO-001".
const fmtPo = (n) => `PO-${String(n ?? 0).padStart(3, '0')}`;

// Real units only (owner picks from a dropdown - free text invited typos like
// pc/pcs/pieces splitting the same unit). Most common first, then grouped.
const UNIT_GROUPS = [
  ['Common', ['pc', 'unit', 'g', 'kg', 'ml', 'L', 'oz', 'lb']],
  ['Count', ['dozen', 'pack', 'box', 'case', 'bag', 'bottle', 'can', 'jar', 'tray', 'roll', 'sachet', 'slice', 'serving']],
  ['Weight', ['mg']],
  ['Volume', ['fl oz', 'cup', 'tbsp', 'tsp', 'pt', 'qt', 'gal']],
];
const ALL_UNITS = UNIT_GROUPS.flatMap(([, units]) => units);

// <option> markup for the unit dropdown. A legacy free-text unit that is not
// in the canonical list stays selectable so existing items keep their value.
const unitSelectOptions = (selected) => {
  const current = (selected ?? '').trim();
  const legacy = current && !ALL_UNITS.includes(current)
    ? `<option value="${esc(current)}" selected>${esc(current)} (current)</option>`
    : '';
  return legacy + UNIT_GROUPS.map(([label, units]) => `
    <optgroup label="${label}">
      ${units.map((u) => `<option value="${u}"${u === current ? ' selected' : ''}>${u}</option>`).join('')}
    </optgroup>`).join('');
};

const TILE_COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ec4899', '#8b5cf6', '#06b6d4'];
const tileColor = (name) => {
  let hash = 0;
  for (const ch of name) hash = (hash * 31 + ch.charCodeAt(0)) & 0xffffffff;
  return TILE_COLORS[Math.abs(hash) % TILE_COLORS.length];
};

const icons = {
  items: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>`,
  po: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>`,
  suppliers: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 3h15v13H1z"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>`,
  counts: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>`,
  history: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`,
  edit: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>`,
  trash: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>`,
  plusCircle: `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>`,
  search: `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>`,
  phone: `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>`,
  mail: `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>`,
};

class InventoryView {
  _panel   = document.querySelector('#inventoryPanel');
  _sidebar = document.querySelector('#inventorySidebar');
  _content = document.querySelector('#inventoryContent');
  _drawer  = document.querySelector('#inventoryDrawer');
  _scrim   = document.querySelector('#inventoryDrawerScrim');
  _section = 'items';       // 'items' | 'po' | 'suppliers' | 'counts' | 'history'
  _query   = '';            // items search
  _statusFilter = 'all';    // items: 'all' | 'low' | 'out'
  _itemSort = 'updated';    // items: 'updated' | 'name' | 'stock' | 'value'
  _itemsPage = 1;
  _itemsPageSize = 25;
  _poQuery = '';
  _poStatusFilter = 'all';  // 'all' | 'draft' | 'sent' | 'overdue' | 'received' | 'cancelled'
  _poDateRange = 'all';     // 'all' | 'today' | 'week' | 'month' | 'custom'
  _poDateFrom = '';
  _poDateTo = '';
  _poPage = 1;
  _poPageSize = 25;
  _supplierQuery = '';
  _countQuery = '';
  _countDraft = new Map();  // itemId -> raw input string (survives re-renders)
  _historyTypeFilter = 'all'; // 'all' | 'sale' | 'po' | 'count' | 'manual'
  _historyDateRange = 'all';  // 'all' | 'today' | 'week' | 'month' | 'custom'
  _historyDateFrom = '';
  _historyDateTo = '';
  _historyPage = 1;
  _historyPageSize = 30;
  _drawerCtx = null;        // { type: 'item'|'supplier'|'po', id }
  _data    = null;          // cached render snapshot

  // Controller callbacks
  _adjustHandler        = null;
  _newItemHandler       = null;
  _editItemHandler      = null;
  _deleteItemHandler    = null;
  _historyFilterHandler = null;
  _addLinkHandler       = null;
  _updateLinkHandler    = null;
  _removeLinkHandler    = null;
  _editProductHandler   = null;
  _saveSupplierHandler  = null;
  _deleteSupplierHandler = null;
  _createPoHandler      = null;
  _updatePoHandler      = null;
  _poStatusHandler      = null;
  _receivePoHandler     = null;
  _applyCountHandler    = null;
  _toggleEnabledHandler = null;
  _addItemSupplierHandler = null;
  _preferSupplierHandler = null;
  _supplierCostHandler = null;
  _removeItemSupplierHandler = null;

  open() {
    this._panel.classList.remove('hidden', 'cashflow-exiting');
  }

  close() {
    this._closeNavDrawer();
    this._closeDetail();
    this._panel.classList.add('cashflow-exiting');
    setTimeout(() => {
      this._panel.classList.add('hidden');
      this._panel.classList.remove('cashflow-exiting');
    }, 220);
  }

  // ── Mobile sidebar nav drawer (shared .panel-drawer-btn pattern) ────────────

  _openNavDrawer() {
    this._sidebar?.classList.add('panel-sidebar--open');
    this._panel.querySelector('#inventoryNavBackdrop')?.classList.add('panel-nav-backdrop--visible');
    this._panel.querySelector('#inventoryNavBtn')?.setAttribute('aria-expanded', 'true');
  }

  _closeNavDrawer() {
    this._sidebar?.classList.remove('panel-sidebar--open');
    this._panel.querySelector('#inventoryNavBackdrop')?.classList.remove('panel-nav-backdrop--visible');
    this._panel.querySelector('#inventoryNavBtn')?.setAttribute('aria-expanded', 'false');
  }

  _addHandlerDrawer() {
    const btn = this._panel.querySelector('#inventoryNavBtn');
    btn?.addEventListener('click', () => {
      if (btn.getAttribute('aria-expanded') === 'true') this._closeNavDrawer();
      else this._openNavDrawer();
    });
    this._panel.querySelector('#inventoryNavBackdrop')?.addEventListener('click', () => this._closeNavDrawer());
    // Detail drawer chrome: scrim click + Escape close.
    this._scrim?.addEventListener('click', () => this._closeDetail());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this._drawerCtx && this.isOpen()) this._closeDetail();
    });
  }

  isOpen() {
    return !this._panel.classList.contains('hidden');
  }

  _addHandlerOpen(handler) {
    document.addEventListener('click', (e) => {
      if (!e.target.closest("[data-action='inventory']")) return;
      handler();
    });
  }

  _addHandlerClose(handler) {
    this._panel.querySelector('.inventory-back')?.addEventListener('click', () => handler());
  }

  _addHandlerAdjust(handler)         { this._adjustHandler = handler; }
  _addHandlerNewItem(handler)        { this._newItemHandler = handler; }
  _addHandlerEditItem(handler)       { this._editItemHandler = handler; }
  _addHandlerDeleteItem(handler)     { this._deleteItemHandler = handler; }
  _addHandlerHistoryFilter(handler)  { this._historyFilterHandler = handler; }
  _addHandlerAddLink(handler)        { this._addLinkHandler = handler; }
  _addHandlerUpdateLink(handler)     { this._updateLinkHandler = handler; }
  _addHandlerRemoveLink(handler)     { this._removeLinkHandler = handler; }
  _addHandlerEditProduct(handler)    { this._editProductHandler = handler; }
  _addHandlerSaveSupplier(handler)   { this._saveSupplierHandler = handler; }
  _addHandlerDeleteSupplier(handler) { this._deleteSupplierHandler = handler; }
  _addHandlerCreatePo(handler)       { this._createPoHandler = handler; }
  _addHandlerUpdatePo(handler)       { this._updatePoHandler = handler; }
  _addHandlerPoStatus(handler)       { this._poStatusHandler = handler; }
  _addHandlerReceivePo(handler)      { this._receivePoHandler = handler; }
  _addHandlerApplyCount(handler)     { this._applyCountHandler = handler; }
  _addHandlerToggleEnabled(handler)  { this._toggleEnabledHandler = handler; }
  _addHandlerAddItemSupplier(handler)    { this._addItemSupplierHandler = handler; }
  _addHandlerPreferSupplier(handler)     { this._preferSupplierHandler = handler; }
  _addHandlerSupplierCost(handler)       { this._supplierCostHandler = handler; }
  _addHandlerRemoveItemSupplier(handler) { this._removeItemSupplierHandler = handler; }

  // Single data entry point.
  // { items, links, adjustments, menuItems, suppliers, purchaseOrders,
  //   historyFilter, invoiceLabelFor }
  render(data) {
    this._data = data;
    this._render();
  }

  // Re-render from fresh model state (other-terminal broadcast or local
  // mutation). Keeps the current section and re-renders any open drawer from
  // the fresh entity (closing it if the entity is gone).
  refreshIfOpen(data) {
    if (!this.isOpen()) return;
    if (data) this._data = data;
    this._render();
  }

  // ── Deep links (external callers, e.g. the notification bell) ──────────────

  // Jump straight to the Purchase Orders section, filtered to drafts (the
  // ones needing approval). Shared by the PO-approval banner's Review button
  // and the notification bell.
  showDraftPOs() {
    this._section = 'po';
    this._poStatusFilter = 'draft';
    this._poPage = 1;
    this._closeDetail();
    this._render();
  }

  // Jump to one specific PO's detail drawer (notification bell item click).
  focusPO(id) {
    this.showDraftPOs();
    this._openDetail('po', id);
  }

  // ── Stock-state helpers ─────────────────────────────────────────────────────

  _state(item) {
    if (item.quantity <= 0) return 'out';
    if (item.lowStockThreshold != null && item.quantity <= item.lowStockThreshold) return 'low';
    return 'ok';
  }

  _statePill(item) {
    const s = this._state(item);
    if (s === 'out') return `<span class="inv-pill inv-pill--out">Out of stock</span>`;
    if (s === 'low') return `<span class="inv-pill inv-pill--low">Low stock</span>`;
    return `<span class="inv-pill inv-pill--ok">In stock</span>`;
  }

  _itemValue(item) {
    return item.unitCost == null ? null : item.quantity * item.unitCost;
  }

  // Incoming stock for an item = the sum of its lines on SENT purchase orders
  // (already ordered, awaiting delivery). Drafts don't count - nothing is on
  // its way yet.
  _incomingForItem(itemId) {
    let qty = 0;
    const pos = [];
    (this._data?.purchaseOrders ?? []).forEach((p) => {
      if (p.status !== 'sent') return;
      const lineQty = (p.lines ?? [])
        .filter((l) => l.inventoryItemId === itemId && l.qty > 0)
        .reduce((s, l) => s + l.qty, 0);
      if (lineQty > 0) { qty += lineQty; pos.push(p); }
    });
    return { qty, pos };
  }

  // Suggested reorder quantity: enough to get back to twice the reorder point
  // (at least one reorder point's worth). No reorder point = leave it blank.
  _suggestReorderQty(item) {
    if (item.lowStockThreshold == null || item.lowStockThreshold <= 0) return '';
    return Math.max(
      Math.ceil(item.lowStockThreshold * 2 - item.quantity),
      Math.ceil(item.lowStockThreshold),
    );
  }

  // Open the PO form pre-filled with these items (reorder flows).
  _openReorderPo(itemsToOrder, supplierId = null) {
    const prefill = itemsToOrder.map((item) => ({
      inventoryItemId: item.id,
      qty: this._suggestReorderQty(item),
      unitCost: item.unitCost,
    }));
    this._showPoModal(null, { prefill, supplierId });
  }

  // PO display status: 'overdue' is DERIVED (sent + expected date past), never stored.
  _poDisplayStatus(po) {
    if (po.status === 'sent' && po.expectedDate) {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      if (new Date(po.expectedDate + 'T23:59:59') < today) return 'overdue';
    }
    return po.status;
  }

  // Display labels for PO statuses (stored values stay draft/sent/received/
  // cancelled - these are friendlier owner-facing terms, Kelly's wording).
  _poPill(po) {
    const s = this._poDisplayStatus(po);
    const meta = {
      draft: ['draft', 'For approval'], sent: ['sent', 'Waiting for delivery'], overdue: ['overdue', 'Overdue'],
      received: ['received', 'Received'], cancelled: ['cancelled', 'Cancelled'],
    }[s] ?? ['draft', s];
    return `<span class="inv-pill inv-pill--${meta[0]}">${meta[1]}</span>`;
  }

  _poTotal(po) {
    const lines = (po.lines ?? []).reduce((s, l) => s + l.qty * (l.unitCost ?? 0), 0);
    return lines + (po.tax ?? 0) + (po.shipping ?? 0);
  }

  _supplierPill(s) {
    const meta = {
      active: ['s-active', 'Active'], preferred: ['s-preferred', 'Preferred'], on_hold: ['s-onhold', 'On hold'],
    }[s.status] ?? ['s-active', 'Active'];
    return `<span class="inv-pill inv-pill--${meta[0]}">${meta[1]}</span>`;
  }

  // ── Shared: stat tiles, date filtering, pagination ──────────────────────────

  // color: 'green' | 'amber' | 'red' | 'blue' | 'muted'
  _statTile({ color, icon, value, label, dataStat = null }) {
    return `
      <div class="invx-stat invx-stat--${color}">
        <span class="invx-stat-accent" aria-hidden="true"></span>
        <span class="invx-stat-icwrap"><span class="invx-stat-icon">${icon}</span></span>
        <div class="invx-stat-main">
          <div class="invx-stat-num"${dataStat ? ` data-stat="${dataStat}"` : ''}>${value}</div>
          <div class="invx-stat-lbl">${label}</div>
        </div>
      </div>`;
  }

  // Shared date-range dropdown + optional custom from/to inputs, used by the
  // Purchase Orders and History toolbars.
  _dateRangeControls(idPrefix, range, from, to) {
    const opts = [['all', 'All time'], ['today', 'Today'], ['week', 'This week'], ['month', 'This month'], ['custom', 'Custom range…']];
    return `
      <select id="${idPrefix}Range" class="inv-cat-select">
        ${opts.map(([v, l]) => `<option value="${v}"${range === v ? ' selected' : ''}>${l}</option>`).join('')}
      </select>
      ${range === 'custom' ? `
        <input type="date" id="${idPrefix}From" class="inv-cat-select" value="${esc(from)}" aria-label="From date" />
        <span class="inv-date-sep">to</span>
        <input type="date" id="${idPrefix}To" class="inv-cat-select" value="${esc(to)}" aria-label="To date" />` : ''}`;
  }

  _dateRangeBounds(range, from, to) {
    const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
    if (range === 'today') return { start: startOfToday, end: null };
    if (range === 'week') { const d = new Date(startOfToday); d.setDate(d.getDate() - 6); return { start: d, end: null }; }
    if (range === 'month') { const d = new Date(startOfToday); d.setDate(d.getDate() - 29); return { start: d, end: null }; }
    if (range === 'custom') return {
      start: from ? new Date(from + 'T00:00:00') : null,
      end: to ? new Date(to + 'T23:59:59') : null,
    };
    return { start: null, end: null };
  }

  _inDateRange(iso, range, from, to) {
    const { start, end } = this._dateRangeBounds(range, from, to);
    if (!start && !end) return true;
    const d = new Date(iso);
    if (start && d < start) return false;
    if (end && d > end) return false;
    return true;
  }

  // Page numbers with ellipsis truncation for large page counts.
  _pageButtons(page, totalPages) {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const set = new Set([1, 2, totalPages - 1, totalPages, page - 1, page, page + 1]);
    const arr = [...set].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
    const out = [];
    arr.forEach((p, i) => { if (i > 0 && p - arr[i - 1] > 1) out.push('…'); out.push(p); });
    return out;
  }

  _pageSlice(arr, page, pageSize) {
    const totalPages = Math.max(1, Math.ceil(arr.length / pageSize));
    const clamped = Math.min(Math.max(1, page), totalPages);
    const start = (clamped - 1) * pageSize;
    return { pageItems: arr.slice(start, start + pageSize), page: clamped, totalPages, total: arr.length, start };
  }

  _pagerHtml(id, { page, totalPages, total, start, count, pageSize, pageSizeOptions, label }) {
    if (!total) return '';
    const from = start + 1;
    const to = start + count;
    const pageBtns = this._pageButtons(page, totalPages).map((p) =>
      p === '…'
        ? `<span class="pg-dots">…</span>`
        : `<button class="pg-btn${p === page ? ' pg-btn--active' : ''}" data-page="${p}" type="button">${p}</button>`
    ).join('');
    return `
      <div class="inv-pager" id="${id}">
        <div class="inv-pager-info">
          Showing <strong>${from.toLocaleString('en-US')}–${to.toLocaleString('en-US')}</strong> of <strong>${total.toLocaleString('en-US')}</strong> ${label}
          <select class="inv-cat-select inv-pager-size" data-role="pagesize">
            ${pageSizeOptions.map((n) => `<option value="${n}"${n === pageSize ? ' selected' : ''}>${n} / page</option>`).join('')}
          </select>
        </div>
        <div class="inv-pager-nav">
          <button class="pg-btn" data-page="${page - 1}" type="button" ${page <= 1 ? 'disabled' : ''}>‹</button>
          ${pageBtns}
          <button class="pg-btn" data-page="${page + 1}" type="button" ${page >= totalPages ? 'disabled' : ''}>›</button>
        </div>
      </div>`;
  }

  _wirePager(id, onPage, onPageSize) {
    const el = document.getElementById(id);
    if (!el) return;
    el.addEventListener('click', (e) => {
      const btn = e.target.closest('.pg-btn');
      if (!btn || btn.disabled) return;
      onPage(parseInt(btn.dataset.page, 10));
    });
    el.querySelector('[data-role="pagesize"]')?.addEventListener('change', (e) => onPageSize(parseInt(e.target.value, 10)));
  }

  // ── Render dispatch ─────────────────────────────────────────────────────────

  _render() {
    if (!this._data) return;
    this._renderSidebar();
    if (this._section === 'history') this._renderHistorySection();
    else if (this._section === 'po') this._renderPoSection();
    else if (this._section === 'suppliers') this._renderSuppliersSection();
    else if (this._section === 'counts') this._renderCountsSection();
    else this._renderItemsSection();
    this._renderDrawer();
  }

  _renderSidebar() {
    const attention = (this._data.items ?? []).filter((i) => this._state(i) !== 'ok').length;
    const overdue = (this._data.purchaseOrders ?? []).filter((p) => this._poDisplayStatus(p) === 'overdue').length;
    const tab = (id, label, count = 0) => `
      <button class="ts-tab${this._section === id ? ' ts-tab--active' : ''}" data-section="${id}" type="button">
        ${icons[id]} ${label}
        ${count ? `<span class="ts-tab-count">${count}</span>` : ''}
      </button>`;
    this._sidebar.innerHTML = `
      <p class="ts-side-label">Sections</p>
      ${tab('items', 'Items', attention)}
      ${tab('po', 'Purchase Orders', overdue)}
      ${tab('suppliers', 'Suppliers')}
      ${tab('counts', 'Stock Counts')}
      ${tab('history', 'History')}`;
    this._sidebar.querySelectorAll('.ts-tab').forEach((btn) =>
      btn.addEventListener('click', () => {
        if (btn.dataset.section === this._section) return this._closeNavDrawer();
        const go = () => {
          this._section = btn.dataset.section;
          this._closeDetail();
          this._render();
          this._closeNavDrawer();
        };
        // Leaving Stock Counts with unsaved counts asks first.
        if (this._section === 'counts' && [...this._countDraft.values()].some((v) => v !== '')) {
          this._showConfirmModal({
            title: 'Leave without applying?',
            message: 'Your typed counts will be discarded.',
            confirmLabel: 'Discard counts',
            onConfirm: () => { this._countDraft.clear(); go(); },
          });
        } else go();
      }));
  }

  // ── Detail drawer (item / supplier / PO) ────────────────────────────────────

  _openDetail(type, id) {
    this._drawerCtx = { type, id };
    this._renderDrawer();
  }

  _closeDetail() {
    this._drawerCtx = null;
    this._drawer?.classList.remove('inv-drawer--open');
    this._scrim?.classList.remove('inv-drawer-scrim--visible');
    this._drawer?.setAttribute('aria-hidden', 'true');
  }

  _renderDrawer() {
    if (!this._drawer) return;
    if (!this._drawerCtx) { this._closeDetail(); return; }
    const { type, id } = this._drawerCtx;
    let html = null;
    if (type === 'item') {
      const item = (this._data.items ?? []).find((i) => i.id === id);
      html = item ? this._itemDrawerHtml(item) : null;
    } else if (type === 'supplier') {
      const s = (this._data.suppliers ?? []).find((x) => x.id === id);
      html = s ? this._supplierDrawerHtml(s) : null;
    } else if (type === 'po') {
      const po = (this._data.purchaseOrders ?? []).find((p) => p.id === id);
      html = po ? this._poDrawerHtml(po) : null;
    }
    if (html == null) { this._closeDetail(); return; }
    this._drawer.innerHTML = html;
    this._drawer.classList.add('inv-drawer--open');
    this._scrim?.classList.add('inv-drawer-scrim--visible');
    this._drawer.setAttribute('aria-hidden', 'false');
    this._drawer.querySelector('.inv-drawer-close')?.addEventListener('click', () => this._closeDetail());
    if (type === 'item') this._wireItemDrawer(id);
    else if (type === 'supplier') this._wireSupplierDrawer(id);
    else if (type === 'po') this._wirePoDrawer(id);
  }

  // ── Section: Items ──────────────────────────────────────────────────────────

  _renderItemsSection() {
    const { items } = this._data;
    const q = this._query.trim().toLowerCase();

    let visible = items;
    if (q) visible = visible.filter((i) =>
      i.name.toLowerCase().includes(q) || (i.sku ?? '').toLowerCase().includes(q));
    if (this._statusFilter === 'low') visible = visible.filter((i) => this._state(i) === 'low');
    if (this._statusFilter === 'out') visible = visible.filter((i) => this._state(i) === 'out');

    visible = [...visible].sort((a, b) => {
      if (this._itemSort === 'name') return a.name.localeCompare(b.name);
      if (this._itemSort === 'stock') return a.quantity - b.quantity;
      if (this._itemSort === 'value') return (this._itemValue(b) ?? -1) - (this._itemValue(a) ?? -1);
      return new Date(b.updatedAt ?? b.createdAt) - new Date(a.updatedAt ?? a.createdAt);
    });

    const lowCount = items.filter((i) => this._state(i) === 'low').length;
    const outCount = items.filter((i) => this._state(i) === 'out').length;
    const costed = items.filter((i) => i.unitCost != null);
    const totalValue = costed.reduce((s, i) => s + i.quantity * i.unitCost, 0);
    const missingCost = items.length - costed.length;

    const { pageItems, page, totalPages, total, start } = this._pageSlice(visible, this._itemsPage, this._itemsPageSize);
    this._itemsPage = page;
    const rows = pageItems.map((item) => this._itemRowHtml(item)).join('');
    const enabled = this._data.inventoryEnabled !== false;

    // Purchase orders needing approval - surfaced right on the Items ("home") tab.
    const draftPos = (this._data.purchaseOrders ?? []).filter((p) => p.status === 'draft');

    this._content.innerHTML = `
      <div class="inv-toggle-card${enabled ? '' : ' inv-toggle-card--off'}">
        <span class="inv-toggle-icon" aria-hidden="true">${enabled ? '🎚️' : '⏸️'}</span>
        <div class="inv-toggle-info">
          <span class="inv-toggle-title">Inventory tracking</span>
          <span class="inv-toggle-sub">${enabled
            ? 'On: sales lower stock, alerts show on the order screen.'
            : 'Off: sales do not lower stock and no alerts show anywhere. Your items and history are kept.'}</span>
        </div>
        <label class="loyalty-enable-toggle" for="invEnabledToggle">
          <input type="checkbox" id="invEnabledToggle" ${enabled ? 'checked' : ''} />
          <span class="loyalty-enable-track" aria-hidden="true"></span>
          <span class="loyalty-enable-text">${enabled ? 'On' : 'Off'}</span>
        </label>
      </div>
      ${(lowCount + outCount > 0 && this._statusFilter === 'all') || draftPos.length ? `
        <div class="inv-attn-band">
          ${lowCount + outCount > 0 && this._statusFilter === 'all' ? `
            <div class="inv-attn">
              <span class="inv-attn-icon" aria-hidden="true">⚠️</span>
              <span class="inv-attn-text"><strong>${lowCount + outCount} item${lowCount + outCount === 1 ? '' : 's'}</strong> need${lowCount + outCount === 1 ? 's' : ''} attention</span>
              <button class="inv-attn-btn" id="invAttnShow" type="button">Show</button>
              <button class="inv-attn-btn inv-attn-btn--primary" id="invAttnReorder" type="button">🛒 Reorder</button>
            </div>` : ''}
          ${draftPos.length ? `
            <div class="inv-attn inv-attn--po">
              <span class="inv-attn-icon" aria-hidden="true">📝</span>
              <span class="inv-attn-text"><strong>${draftPos.length} purchase order${draftPos.length === 1 ? '' : 's'}</strong> need${draftPos.length === 1 ? 's' : ''} approval</span>
              <button class="inv-attn-btn inv-attn-btn--primary" id="invAttnReviewPo" type="button">Review</button>
            </div>` : ''}
        </div>` : ''}
      <div class="invx-stats">
        ${this._statTile({ color: 'green', icon: '📦', value: items.length, label: 'Items tracked', dataStat: 'items' })}
        ${this._statTile({ color: 'amber', icon: '⚠️', value: lowCount, label: 'Low stock', dataStat: 'low' })}
        ${this._statTile({ color: 'red', icon: '🚫', value: outCount, label: 'Out of stock', dataStat: 'out' })}
        ${this._statTile({ color: 'blue', icon: '💰', value: formatMoney(totalValue), label: `Inventory value${missingCost ? ` · ${missingCost} missing cost` : ''}`, dataStat: 'value' })}
      </div>
      <div class="invx-toolbar">
        <div class="invx-search">
          ${icons.search}
          <input type="search" id="invSearchInput" placeholder="Search items or SKU..." autocomplete="off" value="${esc(this._query)}" />
        </div>
        <div class="inv-chiprow" id="invStatusChips">
          ${['all', 'low', 'out'].map((f) => `
            <button class="inv-chip${this._statusFilter === f ? ' inv-chip--active' : ''}" data-filter="${f}" type="button">
              ${f === 'all' ? 'All' : f === 'low' ? 'Low' : 'Out'}
            </button>`).join('')}
        </div>
        <select id="invItemSort" class="inv-cat-select">
          <option value="updated"${this._itemSort === 'updated' ? ' selected' : ''}>Sort: Recently updated</option>
          <option value="name"${this._itemSort === 'name' ? ' selected' : ''}>Sort: Name A–Z</option>
          <option value="stock"${this._itemSort === 'stock' ? ' selected' : ''}>Sort: Stock, low to high</option>
          <option value="value"${this._itemSort === 'value' ? ' selected' : ''}>Sort: Value, high to low</option>
        </select>
        <button class="btn primary invx-new-btn" id="invNewItemBtn" type="button">${icons.plusCircle} New Item</button>
      </div>
      ${visible.length ? `
        <div class="inv-table" id="invItemsTable">
          <div class="inv-thead">
            <span>Item</span><span>Supplier</span><span>On hand</span><span>Reorder point</span>
            <span>Status</span><span>Unit cost</span><span>Value</span><span>Updated</span>
          </div>
          ${rows}
        </div>
        ${this._pagerHtml('invItemsPager', { page, totalPages, total, start, count: pageItems.length, pageSize: this._itemsPageSize, pageSizeOptions: [25, 50, 100], label: 'items' })}` : `
        <div class="invx-empty">
          <div class="invx-empty-icon">📦</div>
          <p class="invx-empty-title">${q || this._statusFilter !== 'all' ? 'No items match.' : 'No inventory items yet'}</p>
          ${q || this._statusFilter !== 'all' ? '' : `<p class="invx-empty-sub">Add the things you keep in stock (milk, cups, beans...) and connect them to your products. Selling a product then lowers its stock automatically.</p>`}
        </div>`}
    `;

    document.getElementById('invSearchInput')?.addEventListener('input', (e) => {
      this._query = e.target.value;
      this._itemsPage = 1;
      const scroll = this._content.scrollTop;
      this._renderItemsSection();
      this._content.scrollTop = scroll;
      const input = document.getElementById('invSearchInput');
      if (input) { input.focus(); input.setSelectionRange(input.value.length, input.value.length); }
    });
    document.getElementById('invEnabledToggle')?.addEventListener('change', (e) => {
      this._toggleEnabledHandler?.(e.target.checked);
    });
    document.getElementById('invNewItemBtn')?.addEventListener('click', () => this._showItemModal());
    document.getElementById('invItemSort')?.addEventListener('change', (e) => {
      this._itemSort = e.target.value;
      this._itemsPage = 1;
      this._renderItemsSection();
    });
    document.getElementById('invAttnShow')?.addEventListener('click', () => {
      this._statusFilter = outCount ? 'out' : 'low';
      this._itemsPage = 1;
      this._renderItemsSection();
    });
    document.getElementById('invAttnReorder')?.addEventListener('click', () => {
      // One order pre-filled with every low/out item (suggested quantities).
      this._openReorderPo(items.filter((i) => this._state(i) !== 'ok'));
    });
    document.getElementById('invAttnReviewPo')?.addEventListener('click', () => this.showDraftPOs());
    document.getElementById('invStatusChips')?.addEventListener('click', (e) => {
      const chip = e.target.closest('.inv-chip');
      if (!chip) return;
      this._statusFilter = chip.dataset.filter;
      this._itemsPage = 1;
      this._renderItemsSection();
    });
    document.getElementById('invItemsTable')?.addEventListener('click', (e) => {
      const row = e.target.closest('.inv-tr');
      if (row) this._openDetail('item', row.dataset.id);
    });
    this._wirePager('invItemsPager',
      (p) => { this._itemsPage = p; this._renderItemsSection(); },
      (n) => { this._itemsPageSize = n; this._itemsPage = 1; this._renderItemsSection(); });
  }

  _itemRowHtml(item) {
    const value = this._itemValue(item);
    const state = this._state(item);
    const initial = (item.name.trim()[0] || '?').toUpperCase();
    const incoming = this._incomingForItem(item.id);
    return `
      <div class="inv-tr" data-id="${item.id}" role="button" tabindex="0">
        <span class="inv-td inv-td--item">
          <span class="invx-tile invx-tile--sm" style="background:${tileColor(item.name)}">${esc(initial)}</span>
          <span class="inv-td-stack">
            <span class="inv-td-name">${esc(item.name)}</span>
            ${item.sku ? `<span class="inv-td-sub">SKU: ${esc(item.sku)}</span>` : ''}
          </span>
        </span>
        <span class="inv-td" data-th="Supplier">${(() => {
          const sup = item.supplierId ? (this._data?.suppliers ?? []).find((s) => s.id === item.supplierId) : null;
          return sup ? esc(sup.name) : '<span class="inv-dim">-</span>';
        })()}</span>
        <span class="inv-td inv-td--qty inv-qty--${state}" data-th="On hand">${fmtQty(item.quantity)} <small>${esc(item.unit)}</small>${incoming.qty > 0 ? `<span class="inv-td-incoming" title="${incoming.pos.map((p) => fmtPo(p.poNumber)).join(', ')} awaiting delivery">🚚 +${fmtQty(incoming.qty)} on order</span>` : ''}</span>
        <span class="inv-td" data-th="Reorder point">${item.lowStockThreshold != null ? `${fmtQty(item.lowStockThreshold)} <small>${esc(item.unit)}</small>` : '<span class="inv-dim">-</span>'}</span>
        <span class="inv-td" data-th="Status">${this._statePill(item)}</span>
        <span class="inv-td" data-th="Unit cost">${item.unitCost != null ? `${formatMoney(item.unitCost)} <small>/ ${esc(item.unit)}</small>` : '<span class="inv-dim">-</span>'}</span>
        <span class="inv-td" data-th="Value">${value != null ? formatMoney(value) : '<span class="inv-dim">-</span>'}</span>
        <span class="inv-td inv-td--updated" data-th="Updated">${fmtRel(item.updatedAt ?? item.createdAt)}</span>
      </div>`;
  }

  // Surgical update after a quantity change: the table row, stat tiles, and
  // (if it shows this item) the open drawer - no list re-render, no blink.
  patchItemRow(item) {
    const cached = this._data?.items?.find((i) => i.id === item.id);
    if (cached) { cached.quantity = item.quantity; cached.updatedAt = new Date().toISOString(); }
    const row = this._content.querySelector(`.inv-tr[data-id="${item.id}"]`);
    if (row && cached) row.outerHTML = this._itemRowHtml(cached);
    this._patchStatTiles();
    if (this._drawerCtx?.type === 'item' && this._drawerCtx.id === item.id) this._renderDrawer();
  }

  _patchStatTiles() {
    const items = this._data?.items ?? [];
    const set = (id, n) => {
      const el = this._content.querySelector(`[data-stat="${id}"]`);
      if (el) el.textContent = n;
    };
    set('items', items.length);
    set('low', items.filter((i) => this._state(i) === 'low').length);
    set('out', items.filter((i) => this._state(i) === 'out').length);
    const costed = items.filter((i) => i.unitCost != null);
    set('value', formatMoney(costed.reduce((s, i) => s + i.quantity * i.unitCost, 0)));
  }

  // ── Item detail drawer ──────────────────────────────────────────────────────

  _itemDrawerHtml(item) {
    const { links, menuItems, suppliers, adjustments } = this._data;
    const value = this._itemValue(item);
    const supplier = item.supplierId ? (suppliers ?? []).find((s) => s.id === item.supplierId) : null;
    const isAuto = !!item.linkedMenuItemId;

    // Used-by: base links resolved against live products.
    const liveProducts = new Map((menuItems ?? []).filter((m) => m.status !== 'deleted').map((m) => [m._id, m]));
    const baseLinks = (links ?? []).filter((l) => l.inventoryItemId === item.id && l.variantLabel == null && liveProducts.has(l.menuItemId));
    const overrideCounts = new Map();
    (links ?? []).forEach((l) => {
      if (l.inventoryItemId === item.id && l.variantLabel != null && liveProducts.has(l.menuItemId))
        overrideCounts.set(l.menuItemId, (overrideCounts.get(l.menuItemId) ?? 0) + 1);
    });

    const usedByRows = baseLinks.map((l) => {
      const product = liveProducts.get(l.menuItemId);
      const locked = isAuto && l.menuItemId === item.linkedMenuItemId;
      const overrides = overrideCounts.get(l.menuItemId) ?? 0;
      return `
        <div class="inv-usedby-row" data-link-id="${l.id}" data-menu-item="${l.menuItemId}">
          <span class="inv-usedby-name">${esc(product.itemName)}</span>
          <span class="inv-usedby-qty">
            ${locked
              ? `<span class="inv-usedby-fixed" title="Managed by this product's Simple count setting">${fmtQty(l.qty)} ${esc(item.unit)} per sale 🔒</span>`
              : `<button class="inv-usedby-qty-btn" data-act="edit-qty" type="button" title="Change amount per sale">${fmtQty(l.qty)} ${esc(item.unit)} per sale ${icons.edit}</button>`}
            ${overrides ? `<button class="inv-usedby-ovr" data-act="edit-product" type="button" title="Edit variant amounts in the product editor">${overrides} variant override${overrides === 1 ? '' : 's'}</button>` : ''}
          </span>
          ${locked ? '' : `<button class="inv-usedby-remove" data-act="remove-link" type="button" aria-label="Unlink product">&times;</button>`}
        </div>`;
    }).join('');

    const recent = (adjustments ?? []).filter((a) => a.inventoryItemId === item.id).slice(0, 5);
    const recentRows = recent.map((a) => `
      <div class="inv-act-row">
        <div class="inv-act-main">
          <span class="inv-act-note">${esc(a.note || this._historyKindLabel(a))}</span>
          <span class="inv-act-meta">${a.staffName ? `${esc(a.staffName)} · ` : ''}${fmtRel(a.createdAt)}</span>
        </div>
        <span class="invx-delta ${a.delta >= 0 ? 'invx-delta--pos' : 'invx-delta--neg'}">${a.delta >= 0 ? '+' : '−'}${fmtQty(Math.abs(a.delta))} ${esc(item.unit)}</span>
      </div>`).join('');

    return `
      <div class="inv-drawer-head">
        <span class="invx-tile" style="background:${tileColor(item.name)}">${esc((item.name.trim()[0] || '?').toUpperCase())}</span>
        <div class="inv-drawer-titles">
          <h3 class="inv-drawer-title">${esc(item.name)}</h3>
          <p class="inv-drawer-sub">${item.sku ? `SKU: ${esc(item.sku)}` : ''}</p>
        </div>
        ${this._statePill(item)}
        <button class="inv-drawer-close" type="button" aria-label="Close details">&times;</button>
      </div>
      <div class="inv-drawer-body">
        <div class="inv-drawer-stats">
          <div class="inv-dstat"><span class="inv-dstat-val inv-qty--${this._state(item)}">${fmtQty(item.quantity)} <small>${esc(item.unit)}</small></span><span class="inv-dstat-lbl">On hand</span></div>
          <div class="inv-dstat"><span class="inv-dstat-val">${item.lowStockThreshold != null ? fmtQty(item.lowStockThreshold) : '-'}</span><span class="inv-dstat-lbl">Reorder point</span></div>
          <div class="inv-dstat"><span class="inv-dstat-val">${item.unitCost != null ? formatMoney(item.unitCost) : '-'}</span><span class="inv-dstat-lbl">Unit cost</span></div>
          <div class="inv-dstat"><span class="inv-dstat-val">${value != null ? formatMoney(value) : '-'}</span><span class="inv-dstat-lbl">Value</span></div>
        </div>
        ${(() => {
          const incoming = this._incomingForItem(item.id);
          if (!incoming.qty) return '';
          return `
          <div class="inv-incoming">
            <span aria-hidden="true">🚚</span>
            <span class="inv-incoming-text"><strong>+${fmtQty(incoming.qty)} ${esc(item.unit)}</strong> already on order ·
              ${incoming.pos.map((p) => `<button class="inv-inline-link" data-act="open-po" data-id="${p.id}" type="button">${fmtPo(p.poNumber)}${p.expectedDate ? ` (${fmtDate(p.expectedDate)})` : ''}</button>`).join(' · ')}
            </span>
          </div>`;
        })()}
        <div class="inv-drawer-actions">
          <button class="btn primary" data-act="receive" type="button">+ Receive stock</button>
          <button class="btn" data-act="remove" type="button">&minus; Use / Remove</button>
          <button class="btn" data-act="reorder" type="button" title="Draft a purchase order for this item">🛒 Reorder</button>
        </div>
        <button class="inv-connect-btn" data-act="add-link" type="button">
          <span class="inv-connect-btn-icon" aria-hidden="true">${icons.plusCircle}</span>
          <span class="inv-connect-btn-text">
            <span class="inv-connect-btn-title">Connect a product</span>
            <span class="inv-connect-btn-sub">Selling that product then lowers this stock automatically</span>
          </span>
        </button>
        <div class="inv-drawer-actions inv-drawer-actions--secondary">
          <button class="btn" data-act="edit-item" type="button">${icons.edit} Edit item</button>
          ${isAuto ? '' : `<button class="btn inv-btn-icon-danger" data-act="delete-item" type="button" aria-label="Delete item">${icons.trash}</button>`}
        </div>
        <h4 class="inv-drawer-section">Suppliers <span class="inv-drawer-section-sub">★ preferred - used for reorders</span></h4>
        ${(() => {
          const rows = (this._data.itemSuppliers ?? [])
            .filter((r) => r.inventoryItemId === item.id)
            .sort((a, b) => (b.preferred ? 1 : 0) - (a.preferred ? 1 : 0));
          const linked = new Set(rows.map((r) => r.supplierId));
          const unlinked = (suppliers ?? []).filter((s) => !linked.has(s.id));
          const rowHtml = rows.map((r) => {
            const s = (suppliers ?? []).find((x) => x.id === r.supplierId);
            if (!s) return '';
            return `
              <div class="inv-isup-row" data-id="${r.id}">
                <button class="inv-isup-star${r.preferred ? ' inv-isup-star--on' : ''}" data-act="prefer-sup" type="button" title="${r.preferred ? 'Preferred supplier' : 'Make preferred'}" aria-label="${r.preferred ? 'Preferred supplier' : 'Make preferred'}">★</button>
                <button class="inv-isup-name" data-act="open-supplier" data-id="${s.id}" type="button">${esc(s.name)}</button>
                <button class="inv-isup-price" data-act="edit-sup-price" type="button" title="Change this supplier's price">${r.unitCost != null ? `${formatMoney(r.unitCost)} / ${esc(item.unit)}` : 'Set price'} ${icons.edit}</button>
                <button class="inv-usedby-remove" data-act="remove-sup" type="button" aria-label="Unlink supplier">&times;</button>
              </div>`;
          }).join('');
          const addHtml = unlinked.length ? `
            <div class="inv-isup-add">
              <select id="invIsupPick" class="inv-cat-select" aria-label="Add a supplier">
                <option value="">Add a supplier...</option>
                ${unlinked.map((s) => `<option value="${s.id}">${esc(s.name)}</option>`).join('')}
              </select>
              <input type="number" id="invIsupPrice" min="0" step="0.01" placeholder="Price / ${esc(item.unit)}" aria-label="Price per ${esc(item.unit)}" />
              <button class="btn" id="invIsupAddBtn" type="button">Add</button>
            </div>` : (suppliers ?? []).length ? '' : `
            <p class="inv-drawer-none">Suppliers are optional. <button class="inv-inline-link" data-act="new-supplier" type="button">Create your first supplier</button></p>`;
          return (rowHtml || `<p class="inv-drawer-none">No suppliers linked yet.</p>`) + addHtml;
        })()}
        <h4 class="inv-drawer-section">Used by <span class="inv-drawer-section-sub">how much one sale uses</span></h4>
        ${usedByRows || `<p class="inv-drawer-none">No products connected yet. Use the green Connect a product button above.</p>`}
        <h4 class="inv-drawer-section">Recent activity</h4>
        ${recentRows || `<p class="inv-drawer-none">No stock changes yet.</p>`}
        ${recent.length ? `<button class="inv-inline-link" data-act="view-history" type="button">View all activity</button>` : ''}
      </div>`;
  }

  _wireItemDrawer(itemId) {
    const item = this._data.items.find((i) => i.id === itemId);
    if (!item) return;
    const d = this._drawer;
    d.querySelector('[data-act="receive"]')?.addEventListener('click', () => this._showAdjustModal(item, +1));
    d.querySelector('[data-act="remove"]')?.addEventListener('click', () => this._showAdjustModal(item, -1));
    d.querySelectorAll('[data-act="edit-item"]').forEach((b) =>
      b.addEventListener('click', () => this._showItemModal(item)));
    d.querySelector('[data-act="delete-item"]')?.addEventListener('click', () => {
      const linked = new Set((this._data.links ?? []).filter((l) => l.inventoryItemId === item.id).map((l) => l.menuItemId)).size;
      this._showConfirmModal({
        title: `Delete ${item.name}?`,
        message: linked
          ? `${linked} linked product${linked === 1 ? '' : 's'} will stop tracking it, and its adjustment history will be removed. This cannot be undone.`
          : 'Its adjustment history will be removed. This cannot be undone.',
        confirmLabel: 'Delete',
        onConfirm: () => { this._closeDetail(); this._deleteItemHandler?.(item.id); },
      });
    });
    d.querySelectorAll('[data-act="open-supplier"]').forEach((b) =>
      b.addEventListener('click', () => this._openDetail('supplier', b.dataset.id)));
    d.querySelector('[data-act="new-supplier"]')?.addEventListener('click', () => this._showSupplierModal());
    // Supplier price list: prefer / inline price edit / unlink / add.
    d.querySelectorAll('.inv-isup-row').forEach((row) => {
      const rowId = row.dataset.id;
      row.querySelector('[data-act="prefer-sup"]')?.addEventListener('click', () =>
        this._preferSupplierHandler?.(rowId));
      row.querySelector('[data-act="remove-sup"]')?.addEventListener('click', () =>
        this._removeItemSupplierHandler?.(rowId));
      row.querySelector('[data-act="edit-sup-price"]')?.addEventListener('click', (e) => {
        const btn = e.currentTarget;
        const current = (this._data.itemSuppliers ?? []).find((r) => r.id === rowId)?.unitCost ?? '';
        const wrap = document.createElement('span');
        wrap.className = 'inv-usedby-editwrap';
        wrap.innerHTML = `
          <input type="number" class="inv-usedby-input" min="0" step="0.01" value="${current}" aria-label="Price per ${esc(item.unit)}" />
          <span class="inv-usedby-unit">/ ${esc(item.unit)}</span>`;
        btn.replaceWith(wrap);
        const input = wrap.querySelector('input');
        input.focus(); input.select();
        const commit = () => {
          const price = input.value === '' ? null : parseFloat(input.value);
          if (price !== current && (price === null || (Number.isFinite(price) && price >= 0)))
            this._supplierCostHandler?.(rowId, price);
          else this._renderDrawer();
        };
        input.addEventListener('keydown', (ev) => {
          if (ev.key === 'Enter') commit();
          if (ev.key === 'Escape') this._renderDrawer();
        });
        input.addEventListener('blur', commit);
      });
    });
    d.querySelector('#invIsupAddBtn')?.addEventListener('click', () => {
      const supplierId = d.querySelector('#invIsupPick')?.value;
      const price = d.querySelector('#invIsupPrice')?.value;
      if (supplierId) this._addItemSupplierHandler?.(item.id, supplierId, price);
    });
    d.querySelector('[data-act="reorder"]')?.addEventListener('click', () =>
      this._openReorderPo([item], item.supplierId ?? null));
    d.querySelectorAll('[data-act="open-po"]').forEach((b) =>
      b.addEventListener('click', () => this._openDetail('po', b.dataset.id)));
    d.querySelector('[data-act="add-link"]')?.addEventListener('click', () => this._showAddLinkModal(item));
    d.querySelector('[data-act="view-history"]')?.addEventListener('click', () => {
      this._section = 'history';
      this._historyFilterHandler?.(item.id);
      this._closeDetail();
    });
    d.querySelectorAll('.inv-usedby-row').forEach((row) => {
      const linkId = row.dataset.linkId;
      const menuItemId = row.dataset.menuItem;
      row.querySelector('[data-act="remove-link"]')?.addEventListener('click', () => {
        const name = row.querySelector('.inv-usedby-name')?.textContent ?? 'this product';
        this._showConfirmModal({
          title: `Unlink ${name}?`,
          message: `Selling ${name} will no longer lower ${item.name} stock.`,
          confirmLabel: 'Unlink',
          onConfirm: () => this._removeLinkHandler?.(linkId),
        });
      });
      row.querySelector('[data-act="edit-product"]')?.addEventListener('click', () =>
        this._editProductHandler?.(menuItemId));
      row.querySelector('[data-act="edit-qty"]')?.addEventListener('click', (e) => {
        // Swap the button for an inline number input.
        const btn = e.currentTarget;
        const current = (this._data.links ?? []).find((l) => l.id === linkId)?.qty ?? 0;
        const wrap = document.createElement('span');
        wrap.className = 'inv-usedby-editwrap';
        wrap.innerHTML = `
          <input type="number" class="inv-usedby-input" min="0" step="any" value="${current}" aria-label="Amount per sale" />
          <span class="inv-usedby-unit">${esc(item.unit)} per sale</span>`;
        btn.replaceWith(wrap);
        const input = wrap.querySelector('input');
        input.focus(); input.select();
        const commit = () => {
          const qty = parseFloat(input.value);
          if (Number.isFinite(qty) && qty > 0 && qty !== current) this._updateLinkHandler?.(linkId, qty);
          else this._renderDrawer(); // revert
        };
        input.addEventListener('keydown', (ev) => {
          if (ev.key === 'Enter') commit();
          if (ev.key === 'Escape') this._renderDrawer();
        });
        input.addEventListener('blur', commit);
      });
    });
  }

  // "Connect a product" picker: search products, pick one, set the per-sale amount.
  _showAddLinkModal(item) {
    const liveProducts = (this._data.menuItems ?? []).filter((m) => m.status !== 'deleted');
    const alreadyLinked = new Set(
      (this._data.links ?? [])
        .filter((l) => l.inventoryItemId === item.id && l.variantLabel == null)
        .map((l) => l.menuItemId),
    );
    const candidates = liveProducts.filter((m) => !alreadyLinked.has(m._id));

    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay-form';
    overlay.style.zIndex = '99999';
    overlay.innerHTML = `
      <div class="invx-modal-card">
        <button class="modal-close-btn" type="button">&times;</button>
        <h3 class="invx-modal-title">Connect a product</h3>
        <p class="invx-modal-sub">Pick a product that uses ${esc(item.name)}, then say how much one sale uses.</p>
        <div class="invx-field">
          <input type="search" id="invLinkSearch" placeholder="Search products..." autocomplete="off" />
        </div>
        <div class="inv-link-results" id="invLinkResults"></div>
        <div class="inv-link-picked hidden" id="invLinkPicked">
          <p class="inv-link-picked-name" id="invLinkPickedName"></p>
          <div class="invx-field">
            <label for="invLinkQty">How much ${esc(item.unit)} does one sale use? <span class="pb-tip" tabindex="0" role="img" aria-label="More info" data-tip="If this product uses the same amount of ${esc(item.name)} no matter the variant, put it here and leave the per-variant fields blank. If the amount depends on the variant, leave this blank and fill the per-variant amounts instead.">i</span></label>
            <input type="number" id="invLinkQty" min="0" step="any" placeholder="e.g. 18" autocomplete="off" />
          </div>
          <div id="invLinkVariants"></div>
        </div>
        <p class="invx-modal-error" id="invLinkError"></p>
        <div class="adj-form-actions">
          <button type="button" class="btn" data-act="cancel">Cancel</button>
          <button type="button" class="btn primary" data-act="save" disabled>Connect</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);
    const close = () => overlay.remove();
    overlay.querySelector('.modal-close-btn').addEventListener('click', close);
    overlay.querySelector('[data-act="cancel"]').addEventListener('click', close);

    let picked = null;
    const resultsEl = overlay.querySelector('#invLinkResults');
    const saveBtn = overlay.querySelector('[data-act="save"]');
    const renderResults = (q) => {
      const query = q.trim().toLowerCase();
      const matches = (query ? candidates.filter((m) => m.itemName.toLowerCase().includes(query)) : candidates).slice(0, 8);
      resultsEl.innerHTML = matches.length
        ? matches.map((m) => `<button class="inv-link-result" data-id="${m._id}" type="button">${esc(m.itemName)}</button>`).join('')
        : `<p class="inv-drawer-none">${candidates.length ? 'No products match.' : 'Every product is already connected.'}</p>`;
      resultsEl.querySelectorAll('.inv-link-result').forEach((btn) =>
        btn.addEventListener('click', () => {
          picked = candidates.find((m) => m._id === btn.dataset.id);
          overlay.querySelector('#invLinkPicked').classList.remove('hidden');
          overlay.querySelector('#invLinkPickedName').textContent = picked.itemName;
          // Variant products get optional per-option amounts (same override
          // rows the product editor writes). Blank = the base amount above;
          // 0 = that option does not use this item at all.
          const variantsEl = overlay.querySelector('#invLinkVariants');
          const groups = (picked.variants ?? []).filter((g) => g?.options?.length);
          variantsEl.innerHTML = groups.length ? `
            <p class="inv-link-var-intro">This product has variants - two ways to set it up:</p>
            <ul class="inv-link-var-rules">
              <li><strong>Same amount for every variant?</strong> Fill the field above, leave these blank.</li>
              <li><strong>Depends on the variant?</strong> (e.g. Large uses more) Type each option's amount below - the field above can stay blank.</li>
              <li>Type <strong>0</strong> for an option that does not use ${esc(item.name)} at all.</li>
            </ul>
            ${groups.map((g) => `
              <div class="inv-link-var-group">
                <p class="inv-link-var-label">${esc(g.optionLabel)}</p>
                ${g.options.map((o) => `
                  <label class="inv-link-var-row">
                    <span class="inv-link-var-name">${esc(o.optionName)}</span>
                    <span class="inv-link-var-input">
                      <input type="number" min="0" step="any" placeholder="base"
                        data-variant-label="${esc(g.optionLabel)}" data-variant-option="${esc(o.optionName)}" />
                      <small>${esc(item.unit)}</small>
                    </span>
                  </label>`).join('')}
              </div>`).join('')}` : '';
          resultsEl.innerHTML = '';
          overlay.querySelector('#invLinkSearch').value = '';
          saveBtn.disabled = false;
          overlay.querySelector('#invLinkQty').focus();
        }));
    };
    renderResults('');
    overlay.querySelector('#invLinkSearch').addEventListener('input', (e) => renderResults(e.target.value));
    overlay.querySelector('#invLinkSearch').focus();

    saveBtn.addEventListener('click', () => {
      const rawQty = overlay.querySelector('#invLinkQty').value;
      const qty = parseFloat(rawQty);
      const errEl = overlay.querySelector('#invLinkError');
      if (!picked) { errEl.textContent = 'Pick a product first.'; return; }
      // Filled per-option inputs become override rows; blanks fall back to base.
      const overrides = [...overlay.querySelectorAll('#invLinkVariants input[data-variant-label]')]
        .filter((input) => input.value !== '' && Number.isFinite(parseFloat(input.value)))
        .map((input) => ({
          variantLabel: input.dataset.variantLabel,
          variantOption: input.dataset.variantOption,
          qty: parseFloat(input.value),
        }));
      const hasBase = Number.isFinite(qty) && qty > 0;
      // Amounts can come from the base field, the per-variant fields, or both -
      // but at least one of them. A blank base with per-variant amounts stores
      // a 0-qty base row (options without their own amount then use nothing).
      if (!hasBase && !overrides.length) {
        errEl.textContent = 'Enter how much one sale uses - either the base amount or the per-variant amounts.';
        return;
      }
      close();
      this._addLinkHandler?.(picked._id, item.id, hasBase ? qty : 0, overrides);
    });
  }

  _requirePoDate(overlay) {
    const errEl = overlay.querySelector('#invPoError');
    const date = overlay.querySelector('#invPoExpected').value;
    if (!date) {
      errEl.textContent = 'Pick the expected or actual delivery date.';
      return null;
    }
    return date;
  }

  // ── Section: Purchase Orders ────────────────────────────────────────────────

  _renderPoSection() {
    const pos = this._data.purchaseOrders ?? [];
    const q = this._poQuery.trim().toLowerCase();

    let visible = pos;
    if (q) visible = visible.filter((p) =>
      fmtPo(p.poNumber).toLowerCase().includes(q) ||
      (p.supplierName ?? '').toLowerCase().includes(q));
    if (this._poStatusFilter !== 'all')
      visible = visible.filter((p) => this._poDisplayStatus(p) === this._poStatusFilter);
    if (this._poDateRange !== 'all')
      visible = visible.filter((p) => this._inDateRange(p.createdAt, this._poDateRange, this._poDateFrom, this._poDateTo));

    const open = pos.filter((p) => p.status === 'draft' || p.status === 'sent');
    const sent = pos.filter((p) => p.status === 'sent');
    const overdue = pos.filter((p) => this._poDisplayStatus(p) === 'overdue');
    const now = new Date();
    const monthTotal = pos
      .filter((p) => p.status !== 'cancelled' &&
        new Date(p.createdAt).getMonth() === now.getMonth() &&
        new Date(p.createdAt).getFullYear() === now.getFullYear())
      .reduce((s, p) => s + this._poTotal(p), 0);

    const { pageItems, page, totalPages, total, start } = this._pageSlice(visible, this._poPage, this._poPageSize);
    this._poPage = page;

    const rows = pageItems.map((po) => `
      <div class="inv-tr inv-tr--po" data-id="${po.id}" role="button" tabindex="0">
        <span class="inv-td inv-td-name">${fmtPo(po.poNumber)}</span>
        <span class="inv-td" data-th="Supplier">${po.supplierName ? esc(po.supplierName) : '<span class="inv-dim">No supplier</span>'}</span>
        <span class="inv-td" data-th="Items">${po.lines.length} item${po.lines.length === 1 ? '' : 's'}</span>
        <span class="inv-td" data-th="Status">${this._poPill(po)}</span>
        <span class="inv-td" data-th="Expected">${po.expectedDate ? fmtDate(po.expectedDate) : '<span class="inv-dim">-</span>'}</span>
        <span class="inv-td" data-th="Total">${formatMoney(this._poTotal(po))}</span>
        <span class="inv-td inv-td--updated" data-th="Created by">${po.createdByName ? esc(po.createdByName) : '-'}</span>
      </div>`).join('');

    const statusChips = [
      ['all', 'All'], ['draft', 'For approval'], ['sent', 'Waiting'],
      ['overdue', 'Overdue'], ['received', 'Received'], ['cancelled', 'Cancelled'],
    ];

    this._content.innerHTML = `
      <div class="invx-stats">
        ${this._statTile({ color: 'blue', icon: '📋', value: open.length, label: 'Open orders' })}
        ${this._statTile({ color: 'amber', icon: '🚚', value: sent.length, label: 'Waiting for delivery' })}
        ${this._statTile({ color: 'red', icon: '⏰', value: overdue.length, label: 'Overdue' })}
        ${this._statTile({ color: 'green', icon: '💰', value: formatMoney(monthTotal), label: 'Ordered this month' })}
      </div>
      <div class="invx-toolbar">
        <div class="invx-search">
          ${icons.search}
          <input type="search" id="invPoSearch" placeholder="Search orders or suppliers..." autocomplete="off" value="${esc(this._poQuery)}" />
        </div>
        <div class="inv-chiprow" id="invPoStatusChips">
          ${statusChips.map(([f, l]) => `
            <button class="inv-chip${this._poStatusFilter === f ? ' inv-chip--active' : ''}" data-filter="${f}" type="button">${l}</button>`).join('')}
        </div>
        <div class="inv-daterow" id="invPoDateRow">
          ${this._dateRangeControls('invPoDate', this._poDateRange, this._poDateFrom, this._poDateTo)}
        </div>
        <button class="btn primary invx-new-btn" id="invNewPoBtn" type="button">${icons.plusCircle} New Purchase Order</button>
      </div>
      ${visible.length ? `
        <div class="inv-table inv-table--po" id="invPoTable">
          <div class="inv-thead">
            <span>Order</span><span>Supplier</span><span>Items</span><span>Status</span>
            <span>Expected</span><span>Total</span><span>Created by</span>
          </div>
          ${rows}
        </div>
        ${this._pagerHtml('invPoPager', { page, totalPages, total, start, count: pageItems.length, pageSize: this._poPageSize, pageSizeOptions: [10, 25, 50], label: 'orders' })}` : `
        <div class="invx-empty">
          <div class="invx-empty-icon">📋</div>
          <p class="invx-empty-title">${q || this._poStatusFilter !== 'all' || this._poDateRange !== 'all' ? 'No orders match.' : 'No purchase orders yet'}</p>
          ${q || this._poStatusFilter !== 'all' || this._poDateRange !== 'all' ? '' : `<p class="invx-empty-sub">Draft an order for the stock you need to buy. When it arrives, mark it received and everything is added to stock automatically.</p>`}
        </div>`}
    `;

    document.getElementById('invPoSearch')?.addEventListener('input', (e) => {
      this._poQuery = e.target.value;
      this._poPage = 1;
      const scroll = this._content.scrollTop;
      this._renderPoSection();
      this._content.scrollTop = scroll;
      const input = document.getElementById('invPoSearch');
      if (input) { input.focus(); input.setSelectionRange(input.value.length, input.value.length); }
    });
    document.getElementById('invNewPoBtn')?.addEventListener('click', () => this._showPoModal());
    document.getElementById('invPoStatusChips')?.addEventListener('click', (e) => {
      const chip = e.target.closest('.inv-chip');
      if (!chip) return;
      this._poStatusFilter = chip.dataset.filter;
      this._poPage = 1;
      this._renderPoSection();
    });
    document.getElementById('invPoDateRow')?.addEventListener('change', (e) => {
      if (e.target.id === 'invPoDateRange') this._poDateRange = e.target.value;
      else if (e.target.id === 'invPoDateFrom') this._poDateFrom = e.target.value;
      else if (e.target.id === 'invPoDateTo') this._poDateTo = e.target.value;
      else return;
      this._poPage = 1;
      this._renderPoSection();
    });
    document.getElementById('invPoTable')?.addEventListener('click', (e) => {
      const row = e.target.closest('.inv-tr');
      if (row) this._openDetail('po', row.dataset.id);
    });
    this._wirePager('invPoPager',
      (p) => { this._poPage = p; this._renderPoSection(); },
      (n) => { this._poPageSize = n; this._poPage = 1; this._renderPoSection(); });
  }

  _poDrawerHtml(po) {
    const supplier = po.supplierId ? (this._data.suppliers ?? []).find((s) => s.id === po.supplierId) : null;
    const linesTotal = (po.lines ?? []).reduce((s, l) => s + l.qty * (l.unitCost ?? 0), 0);
    const lineRows = (po.lines ?? []).map((l) => `
      <div class="inv-po-line">
        ${l.inventoryItemId
          ? `<button class="inv-po-line-name inv-po-line-name--link" data-act="open-line-item" data-id="${l.inventoryItemId}" type="button" title="View this item">${esc(l.itemName)}</button>`
          : `<span class="inv-po-line-name">${esc(l.itemName)}</span>`}
        <span class="inv-po-line-qty">${fmtQty(l.qty)} ${esc(l.unit)}</span>
        <span class="inv-po-line-cost">${l.unitCost != null ? formatMoney(l.unitCost) : '-'}</span>
        <span class="inv-po-line-total">${l.unitCost != null ? formatMoney(l.qty * l.unitCost) : '-'}</span>
      </div>`).join('');

    const actions =
      po.status === 'draft' ? `
        <button class="btn primary" data-act="send" type="button">Approve order</button>
        <button class="btn" data-act="receive" type="button">Mark as received</button>
        <button class="btn" data-act="edit" type="button">${icons.edit} Edit</button>
        <button class="btn inv-btn-text-danger" data-act="cancel-po" type="button">Cancel order</button>`
      : po.status === 'sent' ? `
        <button class="btn primary" data-act="receive" type="button">Mark as received</button>
        <button class="btn inv-btn-text-danger" data-act="cancel-po" type="button">Cancel order</button>`
      : '';

    return `
      <div class="inv-drawer-head">
        <div class="inv-drawer-titles">
          <h3 class="inv-drawer-title">${fmtPo(po.poNumber)}</h3>
          <p class="inv-drawer-sub">${po.supplierName ? esc(po.supplierName) : 'No supplier'}${po.createdByName ? ` · by ${esc(po.createdByName)}` : ''}</p>
        </div>
        ${this._poPill(po)}
        <button class="inv-drawer-close" type="button" aria-label="Close details">&times;</button>
      </div>
      <div class="inv-drawer-body">
        <div class="inv-meta-rows">
          <div class="inv-meta-row"><span>Created</span><span>${fmtDate(po.createdAt)}</span></div>
          ${po.sentAt ? `<div class="inv-meta-row"><span>Sent</span><span>${fmtDate(po.sentAt)}</span></div>` : ''}
          <div class="inv-meta-row"><span>Expected delivery</span><span>${po.expectedDate ? fmtDate(po.expectedDate) : '-'}</span></div>
          ${po.receivedAt ? `<div class="inv-meta-row"><span>Received</span><span>${fmtDate(po.receivedAt)}</span></div>` : ''}
          ${po.cancelledAt ? `<div class="inv-meta-row"><span>Cancelled</span><span>${fmtDate(po.cancelledAt)}</span></div>` : ''}
          ${po.receiptUrl ? `<div class="inv-meta-row"><span>Receipt</span><span><button class="inv-inline-link" data-act="view-receipt" type="button">View receipt</button></span></div>` : ''}
        </div>
        ${supplier && (supplier.phone || supplier.email) ? `
          <p class="inv-drawer-hint">Order with ${esc(supplier.name)} however you normally do:${supplier.phone ? ` ${icons.phone} ${esc(supplier.phone)}` : ''}${supplier.email ? ` · ${icons.mail} ${esc(supplier.email)}` : ''}</p>` : ''}
        <h4 class="inv-drawer-section">Items (${po.lines.length})</h4>
        <div class="inv-po-line inv-po-line--head">
          <span>Item</span><span>Qty</span><span>Unit cost</span><span>Total</span>
        </div>
        ${lineRows || `<p class="inv-drawer-none">No items on this order.</p>`}
        <div class="inv-meta-rows inv-po-totals">
          <div class="inv-meta-row"><span>Subtotal</span><span>${formatMoney(linesTotal)}</span></div>
          ${po.tax != null ? `<div class="inv-meta-row"><span>Tax</span><span>${formatMoney(po.tax)}</span></div>` : ''}
          ${po.shipping != null ? `<div class="inv-meta-row"><span>Shipping</span><span>${formatMoney(po.shipping)}</span></div>` : ''}
          <div class="inv-meta-row inv-meta-row--total"><span>Total</span><span>${formatMoney(this._poTotal(po))}</span></div>
        </div>
        ${po.notes ? `<h4 class="inv-drawer-section">Notes</h4><p class="inv-drawer-notes">${esc(po.notes)}</p>` : ''}
        ${actions ? `<div class="inv-drawer-actions inv-drawer-actions--stack">${actions}</div>` : ''}
      </div>`;
  }

  _wirePoDrawer(poId) {
    const po = (this._data.purchaseOrders ?? []).find((p) => p.id === poId);
    if (!po) return;
    const d = this._drawer;
    d.querySelectorAll('[data-act="open-line-item"]').forEach((b) =>
      b.addEventListener('click', () => this._openDetail('item', b.dataset.id)));
    d.querySelector('[data-act="view-receipt"]')?.addEventListener('click', () =>
      openLightbox(po.receiptUrl, `Receipt for ${fmtPo(po.poNumber)}`));
    d.querySelector('[data-act="send"]')?.addEventListener('click', () => this._poStatusHandler?.(po.id, 'sent'));
    d.querySelector('[data-act="edit"]')?.addEventListener('click', () => this._showPoModal(po));
    d.querySelector('[data-act="cancel-po"]')?.addEventListener('click', () => {
      this._showConfirmModal({
        title: `Cancel ${fmtPo(po.poNumber)}?`,
        message: 'Nothing is added to stock. The order stays in the list as cancelled.',
        confirmLabel: 'Cancel order',
        onConfirm: () => this._poStatusHandler?.(po.id, 'cancelled'),
      });
    });
    d.querySelector('[data-act="receive"]')?.addEventListener('click', () => {
      const n = po.lines.filter((l) => l.inventoryItemId && l.qty > 0).length;
      this._showConfirmModal({
        title: `Receive ${fmtPo(po.poNumber)}?`,
        message: `${n} item${n === 1 ? '' : 's'} will be added to stock. This cannot be undone.`,
        confirmLabel: 'Add to stock',
        onConfirm: () => this._receivePoHandler?.(po.id),
      });
    });
  }

  // Create (po = null) or edit a DRAFT purchase order. The cost column asks
  // for the LINE TOTAL (what you pay for the whole quantity) - the per-piece
  // cost is derived by division, and tax + shipping get spread into the
  // per-piece cost when the order is received (landed cost, server-side).
  _showPoModal(po = null, opts = {}) {
    const isEdit = !!po;
    const items = this._data.items ?? [];
    const suppliers = this._data.suppliers ?? [];
    const preSupplier = po?.supplierId ?? opts.supplierId ?? null;
    const initialLines = po?.lines?.length ? po.lines : opts.prefill?.length ? opts.prefill : [null];
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay-form';
    overlay.style.zIndex = '99999';

    const tip = (text) =>
      `<span class="pb-tip" tabindex="0" role="img" aria-label="More info" data-tip="${esc(text)}">i</span>`;

    // Stored unitCost is per-piece; the form shows the line TOTAL. Each line
    // is a small card: item on top, labeled Qty + Total cost below, with a
    // live "= X each" hint once both are filled.
    const lineRowHtml = (line = null) => {
      const qtyNum = Number(line?.qty);
      const lineTotal = line && line.unitCost != null && Number.isFinite(qtyNum) && qtyNum > 0
        ? Math.round(qtyNum * line.unitCost * 100) / 100
        : '';
      const lineItem = line?.inventoryItemId ? items.find((i) => i.id === line.inventoryItemId) : null;
      const qtyLabel = lineItem ? `Qty (${esc(lineItem.unit)})` : 'Qty';
      return `
      <div class="inv-po-form-line">
        <div class="inv-po-form-line-top">
          <select class="inv-po-item-select" aria-label="Item">
            <option value="">Pick an item...</option>
            <option value="__new__">+ Add a new item...</option>
            ${items.map((i) => `<option value="${i.id}"${line?.inventoryItemId === i.id ? ' selected' : ''}>${esc(i.name)} (${esc(i.unit)})</option>`).join('')}
          </select>
          <button class="inv-usedby-remove" data-act="remove-line" type="button" aria-label="Remove line">&times;</button>
        </div>
        <div class="inv-po-form-line-bottom">
          <label class="inv-po-mini-field">
            <span class="inv-po-qty-label">${qtyLabel}</span>
            <input type="number" class="inv-po-qty" min="0" step="any" placeholder="0" aria-label="Quantity" value="${line?.qty ?? ''}" />
          </label>
          <label class="inv-po-mini-field">
            <span>Total cost (${currencySymbol()})</span>
            <input type="number" class="inv-po-cost" min="0" step="0.01" placeholder="0.00" aria-label="Total cost for this line" value="${lineTotal}" />
          </label>
          <span class="inv-po-line-sum"></span>
        </div>
      </div>`;
    };

    overlay.innerHTML = `
      <div class="invx-modal-card invx-modal-card--wide">
        <button class="modal-close-btn" type="button">&times;</button>
        <h3 class="invx-modal-title">${isEdit ? `Edit ${fmtPo(po.poNumber)}` : 'New Purchase Order'}</h3>
        <div class="invx-field-row">
          <div class="invx-field">
            <label for="invPoSupplier">Supplier</label>
            <select id="invPoSupplier">
              <option value="">No supplier</option>
              ${suppliers.map((s) => `<option value="${s.id}"${preSupplier === s.id ? ' selected' : ''}>${esc(s.name)}</option>`).join('')}
            </select>
          </div>
          <div class="invx-field">
            <label for="invPoExpected">Expected or actual delivery date <span class="inv-req" aria-hidden="true">*</span></label>
            <input type="date" id="invPoExpected" value="${po?.expectedDate ?? new Intl.DateTimeFormat('sv-SE').format(new Date())}" />
          </div>
        </div>
        <p class="adj-form-sublabel">Items to order ${tip('Cost = what you pay for the WHOLE quantity of that line, not per piece. The per-piece cost is worked out automatically.')}</p>
        <div id="invPoLines">${initialLines.map(lineRowHtml).join('')}</div>
        <button class="inv-add-link-btn inv-add-link-btn--strong" id="invPoAddLine" type="button">${icons.plusCircle} Add another item</button>
        <div class="invx-field-row">
          <div class="invx-field">
            <label for="invPoTax">Tax (${currencySymbol()}) ${tip('Spread into each item’s per-piece cost when the order is received, so your inventory value reflects what things really cost you.')}</label>
            <input type="number" id="invPoTax" min="0" step="0.01" placeholder="0" value="${po?.tax ?? ''}" />
          </div>
          <div class="invx-field">
            <label for="invPoShipping">Shipping (${currencySymbol()}) ${tip('Also spread into the per-piece cost on receive, same as tax.')}</label>
            <input type="number" id="invPoShipping" min="0" step="0.01" placeholder="0" value="${po?.shipping ?? ''}" />
          </div>
        </div>
        <div class="invx-field">
          <label for="invPoReceipt">Receipt photo ${tip('A photo of the supplier receipt or invoice, kept with this order. Max 5 MB - big photos are shrunk automatically.')}</label>
          <input type="file" id="invPoReceipt" accept="image/*" />
          ${isEdit && po.receiptUrl ? `<p class="inv-po-receipt-note">A receipt is already attached - choosing a new file replaces it.</p>` : ''}
        </div>
        <div class="invx-field">
          <label for="invPoNotes">Notes</label>
          <textarea id="invPoNotes" rows="2" maxlength="500" placeholder="e.g. Deliver before 10 AM">${po?.notes ? esc(po.notes) : ''}</textarea>
        </div>
        <p class="inv-po-grand" id="invPoGrand"></p>
        <p class="invx-modal-error" id="invPoError"></p>
        <div class="adj-form-actions">
          <button type="button" class="btn" data-act="cancel">Cancel</button>
          ${isEdit ? `
          <button type="button" class="btn primary" data-act="save">Save changes</button>` : `
          <div class="inv-split">
            <button type="button" class="btn primary inv-split-main" data-act="save" data-status="draft">Create - for approval</button>
            <button type="button" class="btn primary inv-split-caret" data-act="save-menu" aria-haspopup="true" aria-expanded="false" aria-label="More create options">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
            </button>
            <div class="inv-split-menu hidden" id="invPoSplitMenu" role="menu">
              <button type="button" role="menuitem" data-status="draft"><strong>For approval</strong><small>Save it - approve later to count it as ordered</small></button>
              <button type="button" role="menuitem" data-status="sent"><strong>Waiting for delivery</strong><small>Already ordered from the supplier</small></button>
              <button type="button" role="menuitem" data-status="received"><strong>Received</strong><small>Already have it - add everything to stock now</small></button>
            </div>
          </div>`}
        </div>
      </div>`;
    document.body.appendChild(overlay);
    const close = () => overlay.remove();
    overlay.querySelector('.modal-close-btn').addEventListener('click', close);
    overlay.querySelector('[data-act="cancel"]').addEventListener('click', close);

    const linesEl = overlay.querySelector('#invPoLines');
    const recalc = () => {
      let total = 0;
      linesEl.querySelectorAll('.inv-po-form-line').forEach((row) => {
        const qty = parseFloat(row.querySelector('.inv-po-qty').value) || 0;
        const lineTotal = parseFloat(row.querySelector('.inv-po-cost').value) || 0;
        // The hint shows the derived per-piece cost (empty until computable).
        const sumEl = row.querySelector('.inv-po-line-sum');
        sumEl.textContent = qty > 0 && lineTotal > 0 ? `= ${formatMoney(lineTotal / qty)} each` : '';
        total += lineTotal;
      });
      total += parseFloat(overlay.querySelector('#invPoTax').value) || 0;
      total += parseFloat(overlay.querySelector('#invPoShipping').value) || 0;
      overlay.querySelector('#invPoGrand').textContent = `Order total: ${formatMoney(total)}`;
    };
    // Prefill the line total from the item's known per-piece cost x qty, but
    // only while the cost box is empty (never fight the user's typing).
    const prefillLine = (row) => {
      const pool = this._data?.items ?? items; // fresh list after a quick-add
      const item = pool.find((i) => i.id === row.querySelector('.inv-po-item-select').value);
      const qty = parseFloat(row.querySelector('.inv-po-qty').value);
      const costEl = row.querySelector('.inv-po-cost');
      if (item && Number.isFinite(qty) && qty > 0 && costEl.value === '') {
        // Prefer the SELECTED supplier's price for this item; fall back to the
        // item's own unit cost.
        const supplierId = overlay.querySelector('#invPoSupplier')?.value || null;
        const supRow = supplierId
          ? (this._data?.itemSuppliers ?? []).find((r) => r.inventoryItemId === item.id && r.supplierId === supplierId)
          : null;
        const per = supRow?.unitCost ?? item.unitCost;
        if (per != null) costEl.value = Math.round(per * qty * 100) / 100;
      }
      // The Qty label speaks the picked item's unit: "Qty (pc)", "Qty (g)"...
      const lbl = row.querySelector('.inv-po-qty-label');
      if (lbl) lbl.textContent = item ? `Qty (${item.unit})` : 'Qty';
    };
    // Rebuild every line's item options from fresh data (after a quick-add).
    const refreshItemSelects = () => {
      const fresh = this._data?.items ?? items;
      overlay.querySelectorAll('.inv-po-item-select').forEach((sel) => {
        const current = sel.value;
        sel.innerHTML = `
          <option value="">Pick an item...</option>
          <option value="__new__">+ Add a new item...</option>
          ${fresh.map((i) => `<option value="${i.id}">${esc(i.name)} (${esc(i.unit)})</option>`).join('')}`;
        sel.value = current;
      });
    };

    linesEl.addEventListener('change', (e) => {
      const select = e.target.closest('.inv-po-item-select');
      const row = e.target.closest('.inv-po-form-line');
      // "+ Add a new item...": quick-create without leaving the order. The new
      // item lands back in THIS line, selected, with its cost prefilled.
      if (select && select.value === '__new__') {
        select.value = ''; // cancelling the quick-add leaves a clean picker
        this._showItemModal(null, {
          onSaved: (created) => {
            refreshItemSelects();
            select.value = created.id;
            prefillLine(row);
            recalc();
          },
        });
        return;
      }
      if (row) prefillLine(row);
      recalc();
    });
    linesEl.addEventListener('input', (e) => {
      const row = e.target.closest('.inv-po-form-line');
      if (row && e.target.classList.contains('inv-po-qty')) prefillLine(row);
      recalc();
    });
    overlay.querySelector('#invPoTax').addEventListener('input', recalc);
    overlay.querySelector('#invPoShipping').addEventListener('input', recalc);
    // Switching supplier re-prefills any still-empty cost boxes with that
    // supplier's prices.
    overlay.querySelector('#invPoSupplier').addEventListener('change', () => {
      linesEl.querySelectorAll('.inv-po-form-line').forEach((row) => prefillLine(row));
      recalc();
    });
    linesEl.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-act="remove-line"]');
      if (!btn) return;
      if (linesEl.querySelectorAll('.inv-po-form-line').length > 1) btn.closest('.inv-po-form-line').remove();
      else btn.closest('.inv-po-form-line').querySelectorAll('input, select').forEach((el) => { el.value = ''; });
      recalc();
    });
    overlay.querySelector('#invPoAddLine').addEventListener('click', () => {
      linesEl.insertAdjacentHTML('beforeend', lineRowHtml());
      recalc();
    });
    recalc();

    // Shared save path. initialStatus: null for edits, else the split-button
    // pick (Gmail-style: the main button saves as "for approval"; the chevron
    // menu offers "waiting for delivery" / "received").
    const doSave = (initialStatus) => {
      const errEl = overlay.querySelector('#invPoError');
      const lines = [...linesEl.querySelectorAll('.inv-po-form-line')].map((row) => {
        const qty = parseFloat(row.querySelector('.inv-po-qty').value);
        const rawTotal = row.querySelector('.inv-po-cost').value;
        const lineTotal = rawTotal === '' ? null : parseFloat(rawTotal);
        return {
          inventoryItemId: row.querySelector('.inv-po-item-select').value || null,
          qty,
          // Stored per-piece: the entered line total divided by quantity.
          unitCost: lineTotal == null || !Number.isFinite(qty) || qty <= 0 ? null : lineTotal / qty,
        };
      }).filter((l) => l.inventoryItemId && Number.isFinite(l.qty) && l.qty > 0);
      if (!lines.length) { errEl.textContent = 'Add at least one item with a quantity.'; return; }
      const expectedDate = this._requirePoDate(overlay);
      if (!expectedDate) return;
      const data = {
        supplierId: overlay.querySelector('#invPoSupplier').value || null,
        expectedDate,
        notes: overlay.querySelector('#invPoNotes').value.trim(),
        tax: overlay.querySelector('#invPoTax').value,
        shipping: overlay.querySelector('#invPoShipping').value,
        receiptFile: overlay.querySelector('#invPoReceipt')?.files?.[0] ?? null,
        initialStatus,
        lines,
      };
      close();
      if (isEdit) this._updatePoHandler?.(po.id, data);
      else this._createPoHandler?.(data);
    };

    if (isEdit) {
      overlay.querySelector('[data-act="save"]').addEventListener('click', () => doSave(null));
    } else {
      overlay.querySelector('.inv-split-main').addEventListener('click', () => doSave('draft'));
      const caret = overlay.querySelector('.inv-split-caret');
      const menu = overlay.querySelector('#invPoSplitMenu');
      caret.addEventListener('click', (e) => {
        e.stopPropagation();
        const open = menu.classList.toggle('hidden');
        caret.setAttribute('aria-expanded', String(!open));
      });
      menu.querySelectorAll('[data-status]').forEach((btn) =>
        btn.addEventListener('click', () => doSave(btn.dataset.status)));
      overlay.addEventListener('click', (e) => {
        if (!e.target.closest('.inv-split')) {
          menu.classList.add('hidden');
          caret.setAttribute('aria-expanded', 'false');
        }
      });
    }
  }

  // ── Section: Suppliers ──────────────────────────────────────────────────────

  _renderSuppliersSection() {
    const suppliers = this._data.suppliers ?? [];
    const pos = this._data.purchaseOrders ?? [];
    const q = this._supplierQuery.trim().toLowerCase();
    const visible = q
      ? suppliers.filter((s) =>
          s.name.toLowerCase().includes(q) ||
          (s.contactName ?? '').toLowerCase().includes(q))
      : suppliers;

    const openPoCount = (id) => pos.filter((p) => p.supplierId === id && (p.status === 'draft' || p.status === 'sent')).length;
    const lastOrder = (id) => {
      const latest = pos.find((p) => p.supplierId === id); // purchaseOrders is newest-first
      return latest ? fmtDate(latest.createdAt) : '-';
    };

    const rows = visible.map((s) => `
      <div class="inv-tr inv-tr--supplier" data-id="${s.id}" role="button" tabindex="0">
        <span class="inv-td inv-td--item">
          <span class="invx-tile invx-tile--sm" style="background:${tileColor(s.name)}">${esc((s.name.trim()[0] || '?').toUpperCase())}</span>
          <span class="inv-td-stack">
            <span class="inv-td-name">${esc(s.name)}</span>
          </span>
        </span>
        <span class="inv-td" data-th="Contact">${s.contactName || s.email || s.phone ? `${s.contactName ? esc(s.contactName) : ''}${s.contactName && (s.email || s.phone) ? '<br/>' : ''}<span class="inv-td-sub">${esc(s.email || s.phone || '')}</span>` : '<span class="inv-dim">-</span>'}</span>
        <span class="inv-td" data-th="Lead time">${s.leadTimeDays != null ? `${s.leadTimeDays} day${s.leadTimeDays === 1 ? '' : 's'}` : '<span class="inv-dim">-</span>'}</span>
        <span class="inv-td" data-th="Payment terms">${s.paymentTerms ? esc(s.paymentTerms) : '<span class="inv-dim">-</span>'}</span>
        <span class="inv-td" data-th="Status">${this._supplierPill(s)}</span>
        <span class="inv-td" data-th="Open POs">${openPoCount(s.id) || '<span class="inv-dim">0</span>'}</span>
        <span class="inv-td inv-td--updated" data-th="Last order">${lastOrder(s.id)}</span>
      </div>`).join('');

    this._content.innerHTML = `
      <div class="invx-stats">
        ${this._statTile({ color: 'green', icon: '🤝', value: suppliers.length, label: 'Suppliers' })}
        ${this._statTile({ color: 'amber', icon: '⭐', value: suppliers.filter((s) => s.status === 'preferred').length, label: 'Preferred' })}
        ${this._statTile({ color: 'muted', icon: '⏸️', value: suppliers.filter((s) => s.status === 'on_hold').length, label: 'On hold' })}
      </div>
      <div class="invx-toolbar">
        <div class="invx-search">
          ${icons.search}
          <input type="search" id="invSupplierSearch" placeholder="Search suppliers..." autocomplete="off" value="${esc(this._supplierQuery)}" />
        </div>
        <button class="btn primary invx-new-btn" id="invNewSupplierBtn" type="button">${icons.plusCircle} Add Supplier</button>
      </div>
      ${visible.length ? `
        <div class="inv-table inv-table--supplier" id="invSupplierTable">
          <div class="inv-thead">
            <span>Supplier</span><span>Contact</span><span>Lead time</span><span>Payment terms</span>
            <span>Status</span><span>Open POs</span><span>Last order</span>
          </div>
          ${rows}
        </div>` : `
        <div class="invx-empty">
          <div class="invx-empty-icon">🤝</div>
          <p class="invx-empty-title">${q ? 'No suppliers match.' : 'No suppliers yet'}</p>
          ${q ? '' : `<p class="invx-empty-sub">Suppliers are optional. Add the vendors you buy from if you want their contact details and purchase orders in one place.</p>`}
        </div>`}
    `;

    document.getElementById('invSupplierSearch')?.addEventListener('input', (e) => {
      this._supplierQuery = e.target.value;
      const scroll = this._content.scrollTop;
      this._renderSuppliersSection();
      this._content.scrollTop = scroll;
      const input = document.getElementById('invSupplierSearch');
      if (input) { input.focus(); input.setSelectionRange(input.value.length, input.value.length); }
    });
    document.getElementById('invNewSupplierBtn')?.addEventListener('click', () => this._showSupplierModal());
    document.getElementById('invSupplierTable')?.addEventListener('click', (e) => {
      const row = e.target.closest('.inv-tr');
      if (row) this._openDetail('supplier', row.dataset.id);
    });
  }

  _supplierDrawerHtml(s) {
    // The SAME item<->supplier price rows the item drawer manages - edited
    // from either side, always in sync (one table underneath).
    const supRows = (this._data.itemSuppliers ?? []).filter((r) => r.supplierId === s.id);
    const linkedItemIds = new Set(supRows.map((r) => r.inventoryItemId));
    const unlinkedItems = (this._data.items ?? []).filter((i) => !linkedItemIds.has(i.id));
    const pos = (this._data.purchaseOrders ?? []).filter((p) => p.supplierId === s.id).slice(0, 5);

    const itemRows = supRows.map((r) => {
      const i = (this._data.items ?? []).find((x) => x.id === r.inventoryItemId);
      if (!i) return '';
      return `
      <div class="inv-isup-row" data-id="${r.id}">
        ${r.preferred ? `<span class="inv-isup-star inv-isup-star--on" title="This supplier is ${esc(i.name)}'s preferred supplier">★</span>` : `<span class="inv-isup-star inv-isup-star--ghost" aria-hidden="true"></span>`}
        <button class="inv-isup-name" data-act="open-item" data-id="${i.id}" type="button">${esc(i.name)}</button>
        <button class="inv-isup-price" data-act="edit-sup-price" data-unit="${esc(i.unit)}" type="button" title="Change the price">${r.unitCost != null ? `${formatMoney(r.unitCost)} / ${esc(i.unit)}` : 'Set price'} ${icons.edit}</button>
        <button class="inv-usedby-remove" data-act="remove-sup" type="button" aria-label="Unlink item">&times;</button>
      </div>`;
    }).join('');
    const addItemHtml = unlinkedItems.length ? `
      <div class="inv-isup-add">
        <select id="invSupItemPick" class="inv-cat-select" aria-label="Add an item this supplier supplies">
          <option value="">Add an item they supply...</option>
          ${unlinkedItems.map((i) => `<option value="${i.id}">${esc(i.name)} (${esc(i.unit)})</option>`).join('')}
        </select>
        <input type="number" id="invSupItemPrice" min="0" step="0.01" placeholder="Price" aria-label="Price" />
        <button class="btn" id="invSupItemAddBtn" type="button">Add</button>
      </div>` : '';
    const poRows = pos.map((p) => `
      <button class="inv-mini-row" data-act="open-po" data-id="${p.id}" type="button">
        <span>${fmtPo(p.poNumber)} · ${fmtDate(p.createdAt)}</span>
        <span class="inv-mini-row-meta">${formatMoney(this._poTotal(p))} ${this._poPill(p)}</span>
      </button>`).join('');

    return `
      <div class="inv-drawer-head">
        <span class="invx-tile" style="background:${tileColor(s.name)}">${esc((s.name.trim()[0] || '?').toUpperCase())}</span>
        <div class="inv-drawer-titles">
          <h3 class="inv-drawer-title">${esc(s.name)}</h3>
          <p class="inv-drawer-sub">Supplier</p>
        </div>
        ${this._supplierPill(s)}
        <button class="inv-drawer-close" type="button" aria-label="Close details">&times;</button>
      </div>
      <div class="inv-drawer-body">
        <div class="inv-meta-rows">
          ${s.contactName ? `<div class="inv-meta-row"><span>Contact</span><span>${esc(s.contactName)}</span></div>` : ''}
          ${s.phone ? `<div class="inv-meta-row"><span>Phone</span><span>${esc(s.phone)}</span></div>` : ''}
          ${s.email ? `<div class="inv-meta-row"><span>Email</span><span>${esc(s.email)}</span></div>` : ''}
          ${s.address ? `<div class="inv-meta-row"><span>Address</span><span>${esc(s.address)}</span></div>` : ''}
          ${s.leadTimeDays != null ? `<div class="inv-meta-row"><span>Lead time</span><span>${s.leadTimeDays} day${s.leadTimeDays === 1 ? '' : 's'}</span></div>` : ''}
          ${s.paymentTerms ? `<div class="inv-meta-row"><span>Payment terms</span><span>${esc(s.paymentTerms)}</span></div>` : ''}
          ${s.minOrder ? `<div class="inv-meta-row"><span>Minimum order</span><span>${esc(s.minOrder)}</span></div>` : ''}
        </div>
        ${s.notes ? `<h4 class="inv-drawer-section">Notes</h4><p class="inv-drawer-notes">${esc(s.notes)}</p>` : ''}
        <h4 class="inv-drawer-section">Items they supply (${supRows.length}) <span class="inv-drawer-section-sub">with their price · ★ = preferred for that item</span></h4>
        ${itemRows || `<p class="inv-drawer-none">No items linked yet - add one below.</p>`}
        ${addItemHtml}
        <h4 class="inv-drawer-section">Recent purchase orders</h4>
        ${poRows || `<p class="inv-drawer-none">No purchase orders yet.</p>`}
        <div class="inv-drawer-actions">
          <button class="btn primary" data-act="new-po" type="button">${icons.plusCircle} New Purchase Order</button>
          <button class="btn" data-act="edit-supplier" type="button">${icons.edit} Edit</button>
          <button class="btn inv-btn-icon-danger" data-act="delete-supplier" type="button" aria-label="Delete supplier">${icons.trash}</button>
        </div>
      </div>`;
  }

  _wireSupplierDrawer(supplierId) {
    const s = (this._data.suppliers ?? []).find((x) => x.id === supplierId);
    if (!s) return;
    const d = this._drawer;
    d.querySelectorAll('[data-act="open-item"]').forEach((b) =>
      b.addEventListener('click', () => this._openDetail('item', b.dataset.id)));
    // Price list rows (same handlers as the item drawer - shared table).
    d.querySelectorAll('.inv-isup-row').forEach((row) => {
      const rowId = row.dataset.id;
      row.querySelector('[data-act="remove-sup"]')?.addEventListener('click', () =>
        this._removeItemSupplierHandler?.(rowId));
      row.querySelector('[data-act="edit-sup-price"]')?.addEventListener('click', (e) => {
        const btn = e.currentTarget;
        const unit = btn.dataset.unit ?? '';
        const current = (this._data.itemSuppliers ?? []).find((r) => r.id === rowId)?.unitCost ?? '';
        const wrap = document.createElement('span');
        wrap.className = 'inv-usedby-editwrap';
        wrap.innerHTML = `
          <input type="number" class="inv-usedby-input" min="0" step="0.01" value="${current}" aria-label="Price" />
          <span class="inv-usedby-unit">/ ${esc(unit)}</span>`;
        btn.replaceWith(wrap);
        const input = wrap.querySelector('input');
        input.focus(); input.select();
        const commit = () => {
          const price = input.value === '' ? null : parseFloat(input.value);
          if (price !== current && (price === null || (Number.isFinite(price) && price >= 0)))
            this._supplierCostHandler?.(rowId, price);
          else this._renderDrawer();
        };
        input.addEventListener('keydown', (ev) => {
          if (ev.key === 'Enter') commit();
          if (ev.key === 'Escape') this._renderDrawer();
        });
        input.addEventListener('blur', commit);
      });
    });
    d.querySelector('#invSupItemAddBtn')?.addEventListener('click', () => {
      const itemId = d.querySelector('#invSupItemPick')?.value;
      const price = d.querySelector('#invSupItemPrice')?.value;
      if (itemId) this._addItemSupplierHandler?.(itemId, s.id, price);
    });
    d.querySelectorAll('[data-act="open-po"]').forEach((b) =>
      b.addEventListener('click', () => this._openDetail('po', b.dataset.id)));
    d.querySelector('[data-act="new-po"]')?.addEventListener('click', () =>
      this._showPoModal(null, { supplierId: s.id }));
    d.querySelector('[data-act="edit-supplier"]')?.addEventListener('click', () => this._showSupplierModal(s));
    d.querySelector('[data-act="delete-supplier"]')?.addEventListener('click', () => {
      this._showConfirmModal({
        title: `Delete ${s.name}?`,
        message: 'Items keep their history and just show no supplier. Past purchase orders keep the name.',
        confirmLabel: 'Delete',
        onConfirm: () => { this._closeDetail(); this._deleteSupplierHandler?.(s.id); },
      });
    });
  }

  _showSupplierModal(s = null) {
    const isEdit = !!s;
    const allItems = this._data?.items ?? [];
    // Edit mode prefills the shared item<->supplier price rows.
    const existingRows = isEdit
      ? (this._data?.itemSuppliers ?? []).filter((r) => r.supplierId === s.id)
      : [];
    const supItemRowHtml = (row = null) => `
      <div class="inv-sup-form-item">
        <select class="inv-sup-item-select" aria-label="Item they supply">
          <option value="">Pick an item...</option>
          ${allItems.map((i) => `<option value="${i.id}"${row?.inventoryItemId === i.id ? ' selected' : ''}>${esc(i.name)} (${esc(i.unit)})</option>`).join('')}
        </select>
        <input type="number" class="inv-sup-item-price" min="0" step="0.01" placeholder="Price / unit" aria-label="Price per unit" value="${row?.unitCost ?? ''}" />
        <button class="inv-usedby-remove" data-act="remove-sup-item" type="button" aria-label="Remove row">&times;</button>
      </div>`;
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay-form';
    overlay.style.zIndex = '99999';
    overlay.innerHTML = `
      <div class="invx-modal-card invx-modal-card--wide">
        <button class="modal-close-btn" type="button">&times;</button>
        <h3 class="invx-modal-title">${isEdit ? 'Edit supplier' : 'Add supplier'}</h3>
        <div class="invx-field">
          <label for="invSupName">Name <span class="inv-req" aria-hidden="true">*</span></label>
          <input type="text" id="invSupName" maxlength="80" placeholder="e.g. Green Bean Co." autocomplete="off" value="${isEdit ? esc(s.name) : ''}" />
        </div>
        <p class="adj-form-sublabel">Items they supply <span class="pb-tip" tabindex="0" role="img" aria-label="More info" data-tip="Pick the inventory items you buy from this supplier and their price per unit. Purchase orders for this supplier prefill these prices. You can also manage this later from the item or supplier details.">i</span></p>
        <div id="invSupItems">${existingRows.length ? existingRows.map(supItemRowHtml).join('') : supItemRowHtml()}</div>
        <button class="inv-add-link-btn inv-add-link-btn--strong" id="invSupAddItemRow" type="button">${icons.plusCircle} Add another item</button>
        <div class="invx-field-row">
          <div class="invx-field">
            <label for="invSupContact">Contact person</label>
            <input type="text" id="invSupContact" data-filter="name" maxlength="80" placeholder="e.g. John Doe" autocomplete="off" value="${isEdit ? esc(s.contactName ?? '') : ''}" />
          </div>
          <div class="invx-field">
            <label for="invSupPhone">Phone</label>
            <input type="tel" id="invSupPhone" maxlength="15" placeholder="e.g. 5551234567" autocomplete="off" value="${isEdit ? esc(s.phone ?? '') : ''}" />
          </div>
        </div>
        <div class="invx-field-row">
          <div class="invx-field">
            <label for="invSupEmail">Email</label>
            <input type="email" id="invSupEmail" maxlength="120" placeholder="e.g. orders@vendor.com" autocomplete="off" value="${isEdit ? esc(s.email ?? '') : ''}" />
          </div>
          <div class="invx-field">
            <label for="invSupStatus">Status</label>
            <select id="invSupStatus">
              <option value="active"${!isEdit || s.status === 'active' ? ' selected' : ''}>Active</option>
              <option value="preferred"${isEdit && s.status === 'preferred' ? ' selected' : ''}>Preferred</option>
              <option value="on_hold"${isEdit && s.status === 'on_hold' ? ' selected' : ''}>On hold</option>
            </select>
          </div>
        </div>
        <div class="invx-field-row">
          <div class="invx-field">
            <label for="invSupLead">Lead time, days <span class="pb-tip" tabindex="0" role="img" aria-label="More info" data-tip="How many days this supplier usually takes to deliver after you place an order. Helps you time reorders.">i</span></label>
            <input type="number" id="invSupLead" min="0" step="1" placeholder="e.g. 3" value="${isEdit && s.leadTimeDays != null ? s.leadTimeDays : ''}" />
          </div>
          <div class="invx-field">
            <label for="invSupMin">Minimum order <span class="pb-tip" tabindex="0" role="img" aria-label="More info" data-tip="The smallest order this supplier accepts, in money or quantity - e.g. 10 kg or 200.">i</span></label>
            <input type="text" id="invSupMin" maxlength="40" placeholder="e.g. 10 kg" autocomplete="off" value="${isEdit ? esc(s.minOrder ?? '') : ''}" />
          </div>
        </div>
        <div class="invx-field-row">
          <div class="invx-field">
            <label for="invSupTerms">Payment terms <span class="pb-tip" tabindex="0" role="img" aria-label="More info" data-tip="When payment is due. Net 30 means you pay within 30 days of the invoice; COD means cash on delivery.">i</span></label>
            <input type="text" id="invSupTerms" maxlength="40" placeholder="e.g. Net 30" autocomplete="off" value="${isEdit ? esc(s.paymentTerms ?? '') : ''}" />
          </div>
          <div class="invx-field">
            <label for="invSupAddress">Address</label>
            <input type="text" id="invSupAddress" maxlength="160" autocomplete="off" value="${isEdit ? esc(s.address ?? '') : ''}" />
          </div>
        </div>
        <div class="invx-field">
          <label for="invSupNotes">Notes</label>
          <textarea id="invSupNotes" rows="2" maxlength="500" placeholder="e.g. Call before delivery">${isEdit && s.notes ? esc(s.notes) : ''}</textarea>
        </div>
        <p class="invx-modal-error" id="invSupError"></p>
        <div class="adj-form-actions">
          <button type="button" class="btn" data-act="cancel">Cancel</button>
          <button type="button" class="btn primary" data-act="save">${isEdit ? 'Save' : 'Add supplier'}</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);
    const close = () => overlay.remove();
    overlay.querySelector('.modal-close-btn').addEventListener('click', close);
    overlay.querySelector('[data-act="cancel"]').addEventListener('click', close);
    overlay.querySelector('#invSupName').focus();

    const supItemsEl = overlay.querySelector('#invSupItems');
    overlay.querySelector('#invSupAddItemRow')?.addEventListener('click', () => {
      supItemsEl.insertAdjacentHTML('beforeend', supItemRowHtml());
    });
    supItemsEl?.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-act="remove-sup-item"]');
      if (!btn) return;
      if (supItemsEl.querySelectorAll('.inv-sup-form-item').length > 1) btn.closest('.inv-sup-form-item').remove();
      else btn.closest('.inv-sup-form-item').querySelectorAll('input, select').forEach((el) => { el.value = ''; });
    });

    overlay.querySelector('[data-act="save"]').addEventListener('click', () => {
      const errEl = overlay.querySelector('#invSupError');
      const name = overlay.querySelector('#invSupName').value.trim();
      if (!name) { errEl.textContent = 'Give the supplier a name.'; return; }
      const email = overlay.querySelector('#invSupEmail').value.trim();
      if (email && !email.includes('@')) { errEl.textContent = 'That email address does not look right.'; return; }
      // Supplied-items rows: first occurrence wins on duplicates.
      const seen = new Set();
      const supplyItems = [...supItemsEl.querySelectorAll('.inv-sup-form-item')]
        .map((row) => ({
          inventoryItemId: row.querySelector('.inv-sup-item-select').value || null,
          unitCost: row.querySelector('.inv-sup-item-price').value,
        }))
        .filter((r) => {
          if (!r.inventoryItemId || seen.has(r.inventoryItemId)) return false;
          seen.add(r.inventoryItemId);
          return true;
        });
      close();
      this._saveSupplierHandler?.(isEdit ? s.id : null, {
        supplyItems,
        name,
        contactName: overlay.querySelector('#invSupContact').value,
        phone: overlay.querySelector('#invSupPhone').value,
        email,
        address: overlay.querySelector('#invSupAddress').value,
        leadTimeDays: overlay.querySelector('#invSupLead').value,
        paymentTerms: overlay.querySelector('#invSupTerms').value,
        minOrder: overlay.querySelector('#invSupMin').value,
        status: overlay.querySelector('#invSupStatus').value,
        notes: overlay.querySelector('#invSupNotes').value,
      });
    });
  }

  // ── Section: Stock Counts ───────────────────────────────────────────────────

  _renderCountsSection() {
    const items = this._data.items ?? [];
    const q = this._countQuery.trim().toLowerCase();
    const visible = q ? items.filter((i) => i.name.toLowerCase().includes(q)) : items;

    const diffs = this._countDiffs();

    const rows = visible.map((item) => {
      const raw = this._countDraft.get(item.id) ?? '';
      const counted = raw === '' ? null : parseFloat(raw);
      const hasDiff = counted != null && Number.isFinite(counted) && counted !== item.quantity;
      const delta = hasDiff ? counted - item.quantity : 0;
      return `
        <div class="inv-count-row" data-id="${item.id}">
          <span class="invx-tile invx-tile--sm" style="background:${tileColor(item.name)}">${esc((item.name.trim()[0] || '?').toUpperCase())}</span>
          <span class="inv-count-name">${esc(item.name)}</span>
          <span class="inv-count-onhand">${fmtQty(item.quantity)} ${esc(item.unit)}</span>
          <input type="number" class="inv-count-input" min="0" step="any" placeholder="Counted" value="${esc(raw)}" aria-label="Counted quantity for ${esc(item.name)}" />
          <span class="inv-count-diff ${hasDiff ? (delta > 0 ? 'invx-delta--pos' : 'invx-delta--neg') : 'inv-count-diff--none'}">
            ${hasDiff ? `${delta > 0 ? '+' : '−'}${fmtQty(Math.abs(delta))}` : ''}
          </span>
        </div>`;
    }).join('');

    this._content.innerHTML = `
      <p class="inv-count-intro">Walk your shelves and type what you actually have. Blank = skip. Nothing changes until you apply.</p>
      <div class="invx-toolbar">
        <div class="invx-search">
          ${icons.search}
          <input type="search" id="invCountSearch" placeholder="Search items..." autocomplete="off" value="${esc(this._countQuery)}" />
        </div>
      </div>
      ${visible.length ? `<div class="inv-count-list">${rows}</div>` : `
        <div class="invx-empty">
          <div class="invx-empty-icon">✅</div>
          <p class="invx-empty-title">${q ? 'No items match.' : 'Nothing to count yet'}</p>
          ${q ? '' : `<p class="invx-empty-sub">Add inventory items first, then use this section to reconcile the real shelf counts.</p>`}
        </div>`}
      <div class="inv-count-footer${diffs.length ? ' inv-count-footer--active' : ''}" id="invCountFooter">
        <span class="inv-count-footer-text">${diffs.length ? `${diffs.length} change${diffs.length === 1 ? '' : 's'} to apply` : 'No changes yet'}</span>
        <button class="btn primary" id="invCountApply" type="button" ${diffs.length ? '' : 'disabled'}>Apply count</button>
      </div>
    `;

    document.getElementById('invCountSearch')?.addEventListener('input', (e) => {
      this._countQuery = e.target.value;
      const scroll = this._content.scrollTop;
      this._renderCountsSection();
      this._content.scrollTop = scroll;
      const input = document.getElementById('invCountSearch');
      if (input) { input.focus(); input.setSelectionRange(input.value.length, input.value.length); }
    });
    this._content.querySelectorAll('.inv-count-input').forEach((input) => {
      input.addEventListener('input', () => {
        const row = input.closest('.inv-count-row');
        this._countDraft.set(row.dataset.id, input.value);
        // Patch just this row's diff chip + the footer (keep typing focus).
        const item = items.find((i) => i.id === row.dataset.id);
        const counted = input.value === '' ? null : parseFloat(input.value);
        const hasDiff = item && counted != null && Number.isFinite(counted) && counted !== item.quantity;
        const delta = hasDiff ? counted - item.quantity : 0;
        const chip = row.querySelector('.inv-count-diff');
        chip.className = `inv-count-diff ${hasDiff ? (delta > 0 ? 'invx-delta--pos' : 'invx-delta--neg') : 'inv-count-diff--none'}`;
        chip.textContent = hasDiff ? `${delta > 0 ? '+' : '−'}${fmtQty(Math.abs(delta))}` : '';
        const diffsNow = this._countDiffs();
        const footer = document.getElementById('invCountFooter');
        footer.classList.toggle('inv-count-footer--active', diffsNow.length > 0);
        footer.querySelector('.inv-count-footer-text').textContent =
          diffsNow.length ? `${diffsNow.length} change${diffsNow.length === 1 ? '' : 's'} to apply` : 'No changes yet';
        document.getElementById('invCountApply').disabled = diffsNow.length === 0;
      });
    });
    document.getElementById('invCountApply')?.addEventListener('click', () => this._showCountConfirm());
  }

  _countDiffs() {
    const items = this._data?.items ?? [];
    const diffs = [];
    for (const [id, raw] of this._countDraft) {
      if (raw === '') continue;
      const counted = parseFloat(raw);
      if (!Number.isFinite(counted) || counted < 0) continue;
      const item = items.find((i) => i.id === id);
      if (!item || counted === item.quantity) continue;
      diffs.push({ item, counted, delta: counted - item.quantity });
    }
    return diffs;
  }

  _showCountConfirm() {
    const diffs = this._countDiffs();
    if (!diffs.length) return;
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay-form';
    overlay.style.zIndex = '99999';
    overlay.innerHTML = `
      <div class="invx-modal-card">
        <button class="modal-close-btn" type="button">&times;</button>
        <h3 class="invx-modal-title">Apply this count?</h3>
        <p class="invx-modal-sub">Stock will be set to what you counted. Every change is logged in History.</p>
        <div class="inv-count-summary">
          ${diffs.map((d) => `
            <div class="inv-count-sum-row">
              <span>${esc(d.item.name)}</span>
              <span class="inv-count-sum-move">${fmtQty(d.item.quantity)} → <strong>${fmtQty(d.counted)}</strong> ${esc(d.item.unit)}</span>
              <span class="invx-delta ${d.delta > 0 ? 'invx-delta--pos' : 'invx-delta--neg'}">${d.delta > 0 ? '+' : '−'}${fmtQty(Math.abs(d.delta))}</span>
            </div>`).join('')}
        </div>
        <div class="adj-form-actions">
          <button type="button" class="btn" data-act="cancel">Go back</button>
          <button type="button" class="btn primary" data-act="apply">Apply ${diffs.length} change${diffs.length === 1 ? '' : 's'}</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);
    const close = () => overlay.remove();
    overlay.querySelector('.modal-close-btn').addEventListener('click', close);
    overlay.querySelector('[data-act="cancel"]').addEventListener('click', close);
    overlay.querySelector('[data-act="apply"]').addEventListener('click', () => {
      close();
      this._countDraft.clear();
      this._applyCountHandler?.(diffs.map((d) => ({ inventory_item_id: d.item.id, counted: d.counted })));
    });
  }

  // ── Section: History ────────────────────────────────────────────────────────

  _historyKindLabel(adj) {
    const { invoiceLabelFor } = this._data ?? {};
    if (adj.kind === 'sale') return adj.receiptNumber != null && invoiceLabelFor ? `Sale ${invoiceLabelFor(adj)}` : 'Sale';
    if (adj.kind === 'void') return adj.receiptNumber != null && invoiceLabelFor ? `Void ${invoiceLabelFor(adj)}` : 'Sale voided';
    if (adj.kind === 'po') return 'Received purchase order';
    if (adj.kind === 'count') return 'Stock count';
    return 'Manual adjustment';
  }

  _renderHistorySection() {
    const { items, adjustments, historyFilter } = this._data;
    const itemOf = (id) => items.find((i) => i.id === id);

    const typeOf = (adj) => adj.kind === 'sale' || adj.kind === 'void' ? 'sale' : (adj.kind ?? 'manual');
    let visible = adjustments ?? [];
    if (this._historyTypeFilter !== 'all') visible = visible.filter((a) => typeOf(a) === this._historyTypeFilter);
    if (this._historyDateRange !== 'all')
      visible = visible.filter((a) => this._inDateRange(a.createdAt, this._historyDateRange, this._historyDateFrom, this._historyDateTo));

    const { pageItems, page, totalPages, total, start } = this._pageSlice(visible, this._historyPage, this._historyPageSize);
    this._historyPage = page;

    const dayKey = (iso) => new Date(iso).toDateString();
    const dayLabel = (iso) => {
      const d = new Date(iso);
      const today = new Date();
      const yest = new Date(Date.now() - 86400000);
      if (d.toDateString() === today.toDateString()) return 'Today';
      if (d.toDateString() === yest.toDateString()) return 'Yesterday';
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    };
    const groups = [];
    pageItems.forEach((adj) => {
      const key = dayKey(adj.createdAt);
      let g = groups[groups.length - 1];
      if (!g || g.key !== key) { g = { key, label: dayLabel(adj.createdAt), rows: [] }; groups.push(g); }
      g.rows.push(adj);
    });

    const rowHtml = (adj) => {
      const item = itemOf(adj.inventoryItemId);
      const name = item?.name ?? '(deleted item)';
      const unit = item?.unit ?? '';
      const initial = (name.trim()[0] || '?').toUpperCase();
      return `
      <li class="invx-hist-row">
        <div class="invx-tile invx-tile--sm" style="background:${item ? tileColor(name) : 'var(--muted)'}">${esc(initial)}</div>
        <div class="invx-hist-main">
          <span class="invx-hist-item">${esc(name)}</span>
          <span class="invx-hist-note">${esc(adj.note || this._historyKindLabel(adj))}</span>
        </div>
        <span class="invx-hist-meta">${adj.staffName ? `${esc(adj.staffName)} · ` : ''}${fmtTime(adj.createdAt)}</span>
        <span class="invx-delta ${adj.delta >= 0 ? 'invx-delta--pos' : 'invx-delta--neg'}">
          ${adj.delta >= 0 ? '+' : '−'}${fmtQty(Math.abs(adj.delta))} ${esc(unit)}
        </span>
      </li>`;
    };

    const typeOpts = [['all', 'All types'], ['sale', 'Sales'], ['po', 'Purchase orders'], ['count', 'Stock counts'], ['manual', 'Manual adjustments']];

    this._content.innerHTML = `
      <div class="invx-toolbar">
        <select id="invHistFilter" class="invx-hist-select">
          <option value="">All items</option>
          ${items.map((i) => `<option value="${i.id}"${historyFilter === i.id ? ' selected' : ''}>${esc(i.name)}</option>`).join('')}
        </select>
        <select id="invHistTypeFilter" class="inv-cat-select">
          ${typeOpts.map(([v, l]) => `<option value="${v}"${this._historyTypeFilter === v ? ' selected' : ''}>${l}</option>`).join('')}
        </select>
        <div class="inv-daterow" id="invHistDateRow">
          ${this._dateRangeControls('invHistDate', this._historyDateRange, this._historyDateFrom, this._historyDateTo)}
        </div>
      </div>
      ${groups.length ? groups.map((g) => `
        <p class="invx-day-label">${esc(g.label)}</p>
        <ul class="invx-hist-list">${g.rows.map(rowHtml).join('')}</ul>`).join('') + this._pagerHtml('invHistPager', { page, totalPages, total, start, count: pageItems.length, pageSize: this._historyPageSize, pageSizeOptions: [30, 50, 100], label: 'entries' }) : `
        <div class="invx-empty">
          <div class="invx-empty-icon">🕘</div>
          <p class="invx-empty-title">${this._historyTypeFilter !== 'all' || this._historyDateRange !== 'all' || historyFilter ? 'No history matches.' : 'No history yet'}</p>
          ${this._historyTypeFilter !== 'all' || this._historyDateRange !== 'all' || historyFilter ? '' : `<p class="invx-empty-sub">Every stock change shows up here: sales, voids, purchase orders, counts, and manual adjustments.</p>`}
        </div>`}
    `;

    document.getElementById('invHistFilter')?.addEventListener('change', (e) => {
      this._historyPage = 1;
      this._historyFilterHandler?.(e.target.value || null);
    });
    document.getElementById('invHistTypeFilter')?.addEventListener('change', (e) => {
      this._historyTypeFilter = e.target.value;
      this._historyPage = 1;
      this._renderHistorySection();
    });
    document.getElementById('invHistDateRow')?.addEventListener('change', (e) => {
      if (e.target.id === 'invHistDateRange') this._historyDateRange = e.target.value;
      else if (e.target.id === 'invHistDateFrom') this._historyDateFrom = e.target.value;
      else if (e.target.id === 'invHistDateTo') this._historyDateTo = e.target.value;
      else return;
      this._historyPage = 1;
      this._renderHistorySection();
    });
    this._wirePager('invHistPager',
      (p) => { this._historyPage = p; this._renderHistorySection(); },
      (n) => { this._historyPageSize = n; this._historyPage = 1; this._renderHistorySection(); });
  }

  // ── Modals (adjust / item / confirm) ────────────────────────────────────────

  // Quick +/- adjustment. direction: +1 add, -1 remove.
  _showAdjustModal(item, direction) {
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay-form';
    overlay.style.zIndex = '99999';
    overlay.innerHTML = `
      <div class="invx-modal-card">
        <button class="modal-close-btn" type="button">&times;</button>
        <div class="invx-modal-head">
          <div class="invx-tile" style="background:${tileColor(item.name)}">${esc((item.name.trim()[0] || '?').toUpperCase())}</div>
          <div>
            <h3 class="invx-modal-title">${direction > 0 ? 'Receive stock' : 'Use / remove stock'}</h3>
            <p class="invx-modal-sub">${esc(item.name)} · currently ${fmtQty(item.quantity)} ${esc(item.unit)}</p>
          </div>
        </div>
        ${direction > 0 ? `
        <div class="inv-adj-hint">
          <span aria-hidden="true">💡</span>
          <span>Quick add - best for <strong>corrections and recounts</strong>. Restocking after a purchase? <button class="inv-inline-link" data-act="go-po" type="button">Use a Purchase Order</button> instead - it also records the cost, supplier, and receipt.</span>
        </div>` : `
        <div class="inv-adj-hint">
          <span aria-hidden="true">💡</span>
          <span>For <strong>spoilage, waste, and corrections</strong>. No need to remove stock for things you sold - sales lower it automatically.</span>
        </div>`}
        <div class="invx-field">
          <label for="invAdjAmount">Amount (${esc(item.unit)})</label>
          <input type="number" id="invAdjAmount" min="0" step="any" placeholder="0" autocomplete="off" />
        </div>
        <div class="invx-field">
          <label for="invAdjNote">Reason</label>
          <input type="text" id="invAdjNote" placeholder="${direction > 0 ? 'e.g. Weekly delivery' : 'e.g. Spoilage, recount'}" maxlength="120" autocomplete="off" />
        </div>
        <p class="invx-modal-error" id="invAdjError"></p>
        <div class="adj-form-actions">
          <button type="button" class="btn" data-act="cancel">Cancel</button>
          <button type="button" class="btn primary" data-act="save">${direction > 0 ? 'Add' : 'Remove'}</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);
    const close = () => overlay.remove();
    overlay.querySelector('.modal-close-btn').addEventListener('click', close);
    overlay.querySelector('[data-act="cancel"]').addEventListener('click', close);
    // "Use a Purchase Order" shortcut: swap this quick-add for a PO pre-filled
    // with this item (and its supplier), so the restock stays fully tracked.
    overlay.querySelector('[data-act="go-po"]')?.addEventListener('click', () => {
      close();
      this._openReorderPo([item], item.supplierId ?? null);
    });

    const amountEl = overlay.querySelector('#invAdjAmount');
    amountEl.focus();
    const save = () => {
      const amount = parseFloat(amountEl.value);
      const errEl = overlay.querySelector('#invAdjError');
      if (!Number.isFinite(amount) || amount <= 0) {
        errEl.textContent = 'Enter an amount greater than zero.';
        return;
      }
      const note = overlay.querySelector('#invAdjNote').value.trim();
      close();
      this._adjustHandler?.(item.id, direction * amount, note);
    };
    overlay.querySelector('[data-act="save"]').addEventListener('click', save);
    overlay.addEventListener('keydown', (e) => { if (e.key === 'Enter') save(); });
  }

  // Create (no item) or edit (item passed). Quantity is deliberately absent in
  // edit mode - it only moves via Receive/Use so every change leaves a history
  // row. Optional profile fields: SKU / unit cost / supplier.
  // opts.onSaved(item) fires after a CREATE completes (the PO form's quick-add
  // uses it to select the new item in place).
  _showItemModal(item = null, opts = {}) {
    const isEdit = !!item;
    const suppliers = this._data?.suppliers ?? [];
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay-form';
    overlay.style.zIndex = '99999';
    overlay.innerHTML = `
      <div class="invx-modal-card invx-modal-card--wide">
        <button class="modal-close-btn" type="button">&times;</button>
        <h3 class="invx-modal-title">${isEdit ? 'Edit item' : 'New inventory item'}</h3>
        ${isEdit && item.linkedMenuItemId ? `<p class="invx-modal-sub">Auto-created by a product's Simple count mode. The name follows the product.</p>` : ''}
        <div class="invx-field">
          <label for="invItemName">Name <span class="inv-req" aria-hidden="true">*</span></label>
          <input type="text" id="invItemName" placeholder="e.g. Milk" maxlength="80" autocomplete="off" value="${isEdit ? esc(item.name) : ''}" ${isEdit && item.linkedMenuItemId ? 'disabled' : ''} />
        </div>
        <div class="invx-field-row">
          <div class="invx-field">
            <label for="invItemUnit">Unit</label>
            <select id="invItemUnit">${unitSelectOptions(isEdit ? item.unit : 'pc')}</select>
          </div>
          ${isEdit ? `
          <div class="invx-field">
            <label>On hand</label>
            <input type="text" value="${fmtQty(item.quantity)} ${esc(item.unit)}" disabled title="Use Receive / Use-Remove so the change is logged" />
          </div>` : `
          <div class="invx-field">
            <label for="invItemQty">Starting quantity</label>
            <input type="number" id="invItemQty" min="0" step="any" placeholder="0" autocomplete="off" />
          </div>`}
        </div>
        <div class="invx-field-row">
          <div class="invx-field">
            <label for="invItemThreshold">Reorder point <span class="inv-req" aria-hidden="true">*</span> <span class="field-optional">(alerts at this level)</span></label>
            <input type="number" id="invItemThreshold" min="0" step="any" placeholder="e.g. 500" autocomplete="off" value="${isEdit && item.lowStockThreshold != null ? fmtQty(item.lowStockThreshold) : ''}" />
          </div>
          <div class="invx-field">
            <label for="invItemCost">Unit cost (${currencySymbol()})</label>
            <input type="number" id="invItemCost" min="0" step="0.01" placeholder="e.g. 12.50" autocomplete="off" value="${isEdit && item.unitCost != null ? item.unitCost : ''}" />
          </div>
        </div>
        <div class="invx-field-row">
          <div class="invx-field">
            <label for="invItemSku">SKU</label>
            <input type="text" id="invItemSku" maxlength="40" placeholder="e.g. CB-001" autocomplete="off" spellcheck="false" value="${isEdit ? esc(item.sku ?? '') : ''}" />
          </div>
        </div>
        <p class="invx-modal-sub">Suppliers and their prices are managed on the item's details panel.</p>
        <p class="invx-modal-error" id="invItemError"></p>
        <div class="adj-form-actions">
          <button type="button" class="btn" data-act="cancel">Cancel</button>
          <button type="button" class="btn primary" data-act="save">${isEdit ? 'Save' : 'Add Item'}</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);
    const close = () => overlay.remove();
    overlay.querySelector('.modal-close-btn').addEventListener('click', close);
    overlay.querySelector('[data-act="cancel"]').addEventListener('click', close);
    overlay.querySelector('#invItemName')?.focus();

    overlay.querySelector('[data-act="save"]').addEventListener('click', () => {
      const errEl = overlay.querySelector('#invItemError');
      const name = overlay.querySelector('#invItemName').value.trim();
      if (!name) { errEl.textContent = 'Give the item a name.'; return; }
      const unit = overlay.querySelector('#invItemUnit').value || 'pc';
      const thresholdRaw = overlay.querySelector('#invItemThreshold').value;
      const lowStockThreshold = parseFloat(thresholdRaw);
      if (!Number.isFinite(lowStockThreshold) || lowStockThreshold <= 0) {
        errEl.textContent = 'Set a reorder point greater than zero.'; return;
      }
      const extras = {
        sku: overlay.querySelector('#invItemSku').value,
        unitCost: overlay.querySelector('#invItemCost').value,
      };
      close();
      if (isEdit) {
        this._editItemHandler?.(item.id, { name, unit, lowStockThreshold, ...extras });
      } else {
        const qtyRaw = overlay.querySelector('#invItemQty').value;
        const quantity = qtyRaw === '' ? 0 : parseFloat(qtyRaw) || 0;
        this._newItemHandler?.({ name, unit, quantity, lowStockThreshold, ...extras }, opts.onSaved);
      }
    });
  }

  _showConfirmModal({ title, message, confirmLabel, onConfirm }) {
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay-form';
    overlay.style.zIndex = '99999';
    overlay.innerHTML = `
      <div class="invx-modal-card">
        <button class="modal-close-btn" type="button">&times;</button>
        <h3 class="invx-modal-title">${esc(title)}</h3>
        <p class="invx-modal-sub">${esc(message)}</p>
        <div class="adj-form-actions">
          <button type="button" class="btn" data-act="cancel">Cancel</button>
          <button type="button" class="btn invx-btn-danger" data-act="confirm">${esc(confirmLabel)}</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);
    const close = () => overlay.remove();
    overlay.querySelector('.modal-close-btn').addEventListener('click', close);
    overlay.querySelector('[data-act="cancel"]').addEventListener('click', close);
    overlay.querySelector('[data-act="confirm"]').addEventListener('click', () => { close(); onConfirm?.(); });
  }
}

const _view = new InventoryView();
_view._addHandlerDrawer();
export default _view;
